import { fmtDate } from '../utils/format';
import { fetchCryptoUsdPrices, formatApproxUsd, formatTokenAmount } from '../utils/cryptoPrices';
import { logActivity } from '../utils/activityLog';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Pencil, Check, X, Plus, Trash2, ChevronDown,
    Send, RefreshCw, Lock, LogIn, Link, KeyRound, ShieldOff,
  FileText, Upload, Eye, EyeOff, Pin, MoreVertical, Phone, CalendarDays, Users
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { userSettingsService } from '../services/userSettings';
import { toast } from 'sonner';
import { STATUS_PALETTE } from './Settings';
import {
  getCurrentUser,
  canEditSales,
  canEditRetention,
  canDeleteComment,
  canEditComment,
  canUseTransactionAdminControls,
  roleDepartment,
  SALES_ROLES,
  RET_ROLES,
} from '../utils/roles';
import UploadPreviewModal from '../components/UploadPreviewModal';

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmtMoney = (n) => (n ?? 0).toFixed(2) + ' $';

const normalizeDialNumber = (value) => {
  const raw = String(value || '').trim();
  if (!raw || raw === '-') return '';
  const hasPlus = raw.startsWith('+');
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  return hasPlus ? `+${digits}` : digits;
};

const openLocalSipDialer = (number) => {
  // Hand the number off to the operator's active softphone (MicroSIP, etc.).
  // Also copy it so the operator can paste / add a prefix / pick a provider there.
  try { navigator.clipboard?.writeText(number); } catch { /* clipboard optional */ }
  window.location.href = `sip:${encodeURIComponent(number)}`;
};

// statusColorMap is built at runtime from /options/statuses; fallback to gray
let _statusColorMap = {};
export const loadStatusColors = async () => {
  try {
    const { data } = await api.get('/options/statuses', { params: { entity_type: 'all' } });
    _statusColorMap = Object.fromEntries((data.statuses || []).map(s => [s.name, STATUS_PALETTE[s.color] || STATUS_PALETTE.gray]));
  } catch { /* ignore */ }
};
const statusColor = (s) => _statusColorMap[s] || STATUS_PALETTE.gray;

// ─── combined name field (display: "First Last", edit: two inputs) ───────────
function NameField({ firstName, lastName, onSave, isDark }) {
  const [editing, setEditing] = useState(false);
  const [first, setFirst] = useState(firstName ?? '');
  const [last, setLast] = useState(lastName ?? '');

  const open = () => { setFirst(firstName ?? ''); setLast(lastName ?? ''); setEditing(true); };
  const cancel = () => setEditing(false);
  const save = async () => {
    if (first !== (firstName ?? '')) await onSave('firstName', first);
    if (last !== (lastName ?? ''))   await onSave('lastName', last);
    setEditing(false);
  };

  const labelCls = `text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`;
  const valCls   = `text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`;
  const inputCls = `text-sm border rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500 w-full ${
    isDark ? 'bg-[#0a0e1a] border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;

  if (!editing) {
    return (
      <div className="flex items-start gap-1 min-w-0">
        <div className="flex-1 min-w-0">
          <p className={labelCls}>Name:</p>
          <p className={valCls}>{[firstName, lastName].filter(Boolean).join(' ') || '—'}</p>
        </div>
        <button onClick={open} className={`mt-4 ${isDark ? 'text-gray-600 hover:text-gray-400' : 'text-gray-300 hover:text-gray-600'}`}>
          <Pencil size={13} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-1 min-w-0 col-span-2">
      <div className="flex-1 min-w-0">
        <p className={labelCls}>Name:</p>
        <div className="flex gap-2 mt-0.5">
          <div className="flex-1">
            <p className={`text-xs mb-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>First</p>
            <input value={first} onChange={e => setFirst(e.target.value)} className={inputCls} autoFocus
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }} />
          </div>
          <div className="flex-1">
            <p className={`text-xs mb-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Last</p>
            <input value={last} onChange={e => setLast(e.target.value)} className={inputCls}
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }} />
          </div>
        </div>
      </div>
      <div className="flex gap-1 mt-7">
        <button onClick={save} className="text-green-500 hover:text-green-400"><Check size={14} /></button>
        <button onClick={cancel} className="text-red-500 hover:text-red-400"><X size={14} /></button>
      </div>
    </div>
  );
}

// ─── dept status card — purely informational, active dept highlighted ──────────
function DeptStatusCard({ deptLabel, deptColor, accentBorder, accentBg, status, agent, desk, team, isDark, isActive, t }) {
  const txtColor = (_statusColorMap[status] || STATUS_PALETTE.gray).split(' ').find(c => c.startsWith('text-')) || 'text-gray-400';
  const subColor = isDark ? 'rgba(255,255,255,0.28)' : '#94a3b8';
  const labelColor = isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8';
  const agentColor = isDark ? 'rgba(255,255,255,0.65)' : '#334155';
  return (
    <div
      className={`rounded-xl border p-3 mb-2 last:mb-0 transition-all ${
        isActive
          ? `${accentBorder}`
          : (isDark ? 'border-gray-800/40' : 'border-gray-100')
      }`}
      style={{ opacity: isActive ? 1 : 0.45 }}
    >
      {/* Two-column layout */}
      <div className="flex items-start gap-3">
        {/* LEFT — dept label + status */}
        <div className="flex-1 min-w-0">
          <span className={`text-[10px] font-bold tracking-[0.18em] uppercase ${deptColor}`}>{deptLabel}</span>
          <p className={`text-base font-bold uppercase tracking-wide mt-0.5 ${
            status ? txtColor : (isDark ? 'text-gray-700' : 'text-gray-300')
          }`}>
            {status || '—'}
          </p>
        </div>

        {/* RIGHT — agent / desk / team (read-only reference) */}
        <div className="shrink-0 text-right space-y-0.5">
          <p className="text-xs font-semibold truncate" style={{ color: agentColor }}>
            {agent || <span style={{ color: isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1' }}>{t('clientDetail.notAssigned')}</span>}
          </p>
          <p className="text-[11px] truncate" style={{ color: subColor }}>
            <span style={{ color: labelColor }}>{t('clientDetail.desk')}: </span>{desk || '—'}
          </p>
          <p className="text-[11px] truncate" style={{ color: subColor }}>
            <span style={{ color: labelColor }}>{t('clientDetail.team')}: </span>{team || '—'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── change status modal — dept driven by user role ───────────────────────────
function ChangeStatusModal({ client, statusOptions, isDark, onSave, onClose, userRole }) {
  const userDept    = roleDepartment(userRole);           // 'sales' | 'retention' | null
  const clientDept  = (client.department || 'sales').toLowerCase();
  const isPrivileged = userDept === null;                 // owner/admin/crm_manager see both tabs

  // default tab: privileged → client's dept; dept user → their own dept
  const [targetDept, setTargetDept] = useState(
    isPrivileged ? (clientDept === 'retention' ? 'retention' : 'sales') : userDept
  );
  const [selected, setSelected] = useState('');
  const [saving, setSaving]     = useState(false);

  // helpers — same fallback used in the status card
  const currentSalesStatus = client.salesStatus || (clientDept !== 'retention' ? client.status : '') || '';
  const currentRetStatus   = client.retStatus   || (clientDept === 'retention'  ? client.status : '') || '';

  useEffect(() => {
    setSelected(targetDept === 'retention' ? currentRetStatus : currentSalesStatus);
  }, [targetDept, client, currentRetStatus, currentSalesStatus]);

  const deptOptions = statusOptions.filter(s =>
    (s.entityType || 'sales').toLowerCase() === targetDept
  );

  const handleSave = async () => {
    setSaving(true);
    await onSave(targetDept === 'retention' ? 'retStatus' : 'salesStatus', selected);
    setSaving(false);
    onClose();
  };

  const deptCfg = {
    sales:     { color: '#60a5fa', bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.35)' },
    retention: { color: '#22d3ee', bg: 'rgba(6,182,212,0.15)',  border: 'rgba(6,182,212,0.35)'  },
  };
  const cfg = deptCfg[targetDept];

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
    >
      <div
        className="w-full max-w-sm rounded-2xl animate-scale-in"
        style={{
          background: isDark ? 'rgba(8,12,22,0.97)' : '#fff',
          border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(99,102,241,0.18)',
          boxShadow: isDark ? '0 32px 80px rgba(0,0,0,0.7)' : '0 32px 80px rgba(99,102,241,0.15)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(99,102,241,0.1)' }}>
          <div>
            <h2 className="text-sm font-semibold" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>
              Change Status
            </h2>
            {/* Show dept badge in header — always visible, not just when privileged */}
            <span
              className="text-[10px] font-bold uppercase tracking-wider"
              style={{ color: cfg.color }}
            >
              {targetDept}
            </span>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}
            onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            <X size={15} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Dept tabs — only for privileged roles */}
          {isPrivileged && (
            <div className="flex gap-2">
              {['sales', 'retention'].map(d => {
                const dc = deptCfg[d];
                const isActive = targetDept === d;
                return (
                  <button key={d} onClick={() => setTargetDept(d)}
                    className="flex-1 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5"
                    style={isActive ? {
                      background: dc.bg, color: dc.color, border: `1px solid ${dc.border}`,
                    } : {
                      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                      color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8',
                      border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.08)',
                    }}
                  >
                    {d}
                    {clientDept === d && (
                      <span className="w-1.5 h-1.5 rounded-full" style={{
                        background: dc.color, boxShadow: `0 0 5px ${dc.color}`,
                        animation: 'pulse-glow 2s ease-in-out infinite',
                      }} />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Current status */}
          <div className="text-xs flex items-center gap-2" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
            Current:
            <span className="font-semibold" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>
              {(targetDept === 'retention' ? currentRetStatus : currentSalesStatus) || '—'}
            </span>
          </div>

          {/* Status pills */}
          {deptOptions.length === 0 ? (
            <p className="text-xs text-center py-4" style={{ color: isDark ? 'rgba(255,255,255,0.2)' : '#94a3b8' }}>
              No statuses configured
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto pr-0.5">
              {deptOptions.map(s => {
                const sName = typeof s === 'object' ? s.name : s;
                const isSelected = selected === sName;
                return (
                  <button key={sName} onClick={() => setSelected(sName)}
                    className="px-3 py-2 rounded-xl text-xs font-semibold text-left transition-all"
                    style={isSelected ? {
                      background: 'rgba(99,102,241,0.2)',
                      border: '1px solid rgba(99,102,241,0.45)',
                      color: '#a5b4fc',
                      boxShadow: '0 0 10px rgba(99,102,241,0.15)',
                    } : {
                      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                      border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)',
                      color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b',
                    }}>
                    {sName}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all"
            style={{
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b',
              border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.08)',
            }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || deptOptions.length === 0}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: '0 0 16px rgba(99,102,241,0.3)' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── transfer client to another agent (Sales / Retention) ─────────────────────
// Reuses the same /clients/bulk/assign path the Clients list uses, scoped to
// this one client. Picking a Sales agent moves the client to Sales, a Retention
// agent moves it to Retention (department is derived server-side from the agent).
function TransferAgentModal({ client, clientId, users, isDark, userRole, onClose, onTransferred }) {
  const userDept     = roleDepartment(userRole);          // 'sales' | 'retention' | null
  const isPrivileged = userDept === null;                 // owner/admin/crm_manager pick the dept
  const clientDept   = (client.department || 'sales').toLowerCase();

  const [dept, setDept]       = useState(isPrivileged ? (clientDept === 'retention' ? 'retention' : 'sales') : userDept);
  const [search, setSearch]   = useState('');
  const [agentId, setAgentId] = useState('');
  const [saving, setSaving]   = useState(false);

  const agentDept = (u) => {
    const d = (u.department || '').toLowerCase();
    if (d === 'sales' || d === 'retention') return d;
    if (SALES_ROLES.includes(u.role)) return 'sales';
    if (RET_ROLES.includes(u.role))   return 'retention';
    return '';
  };

  useEffect(() => { setAgentId(''); }, [dept]);

  const options = users
    .filter(u => agentDept(u) === dept)
    .filter(u => !search || (u.name || '').toLowerCase().includes(search.trim().toLowerCase()));

  const currentAgent = dept === 'retention'
    ? (client.retAgent   || (clientDept === 'retention' ? client.agent : ''))
    : (client.salesAgent || (clientDept !== 'retention' ? client.agent : ''));

  const handleSave = async () => {
    if (!agentId) { toast.error('Select an agent'); return; }
    setSaving(true);
    try {
      await api.post('/clients/bulk/assign', { client_ids: [Number(clientId)], value: agentId });
      const agent = users.find(u => String(u.id) === String(agentId));
      toast.success(`Transferred to ${agent?.name || 'agent'}`);
      onTransferred && onTransferred();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transfer failed');
    } finally {
      setSaving(false);
    }
  };

  const deptCfg = {
    sales:     { color: '#60a5fa', bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.35)' },
    retention: { color: '#22d3ee', bg: 'rgba(6,182,212,0.15)',  border: 'rgba(6,182,212,0.35)'  },
  };
  const inputCls = `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`;

  return (
    <Modal title="Transfer to agent" onClose={onClose} isDark={isDark}>
      <div className="space-y-4">
        {isPrivileged && (
          <div className="flex gap-2">
            {['sales', 'retention'].map(d => {
              const dc = deptCfg[d];
              const isActive = dept === d;
              return (
                <button key={d} onClick={() => setDept(d)}
                  className="flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
                  style={isActive
                    ? { background: dc.bg, color: dc.color, border: `1px solid ${dc.border}` }
                    : { background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8', border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.08)' }}>
                  {d}
                </button>
              );
            })}
          </div>
        )}

        <div className="text-xs flex items-center gap-2" style={{ color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8' }}>
          Current {dept} agent:
          <span className="font-semibold" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>{currentAgent || '—'}</span>
        </div>

        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search agent…" className={inputCls} autoFocus />

        <div className={`max-h-56 overflow-y-auto rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {options.length === 0 ? (
            <p className="text-xs text-center py-6" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>No agents found</p>
          ) : options.map(u => {
            const isSel = String(agentId) === String(u.id);
            return (
              <button key={u.id} onClick={() => setAgentId(u.id)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors ${isSel ? 'bg-purple-500/20' : isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-gray-50'}`}>
                <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>{u.name}</span>
                <span className="text-[10px] uppercase tracking-wide flex-shrink-0" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>
                  {[u.desk, u.team].filter(Boolean).join(' · ') || u.role}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium ${isDark ? 'bg-white/[0.05] text-gray-300 border border-gray-700' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !agentId}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
            {saving ? 'Transferring…' : 'Transfer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── inline-edit field ───────────────────────────────────────────────────────
// groupedOptions: [{label, items: [{name, entityType}]}]
function EditableField({ label, value, field, onSave, isDark, options, groupedOptions }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');

  const save = async () => {
    if (val === (value ?? '')) { setEditing(false); return; }
    await onSave(field, val);
    setEditing(false);
  };

  const labelCls = `text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`;
  const valCls   = `text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`;
  const inputCls = `text-sm border rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-500 ${
    isDark ? 'bg-[#0a0e1a] border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;

  return (
    <div className="flex items-start gap-1 min-w-0">
      <div className="flex-1 min-w-0">
        <p className={labelCls}>{label}:</p>
        {editing ? (
          groupedOptions ? (
            <select value={val} onChange={e => setVal(e.target.value)} className={inputCls} autoFocus>
              <option value="">—</option>
              {groupedOptions.map(grp => (
                <optgroup key={grp.label} label={grp.label}>
                  {grp.items.map(o => {
                    const n = typeof o === 'object' ? o.name : o;
                    return <option key={n} value={n}>{n}</option>;
                  })}
                </optgroup>
              ))}
            </select>
          ) : options ? (
            <select value={val} onChange={e => setVal(e.target.value)} className={inputCls} autoFocus>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input value={val} onChange={e => setVal(e.target.value)} className={inputCls} autoFocus
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />
          )
        ) : (
          <p className={valCls}>{value || '—'}</p>
        )}
      </div>
      {editing ? (
        <div className="flex gap-1 mt-4">
          <button onClick={save} className="text-green-500 hover:text-green-400"><Check size={14} /></button>
          <button onClick={() => { setVal(value ?? ''); setEditing(false); }} className="text-red-500 hover:text-red-400"><X size={14} /></button>
        </div>
      ) : (
        <button onClick={() => { setVal(value ?? ''); setEditing(true); }}
          className={`mt-4 ${isDark ? 'text-gray-600 hover:text-gray-400' : 'text-gray-300 hover:text-gray-600'}`}>
          <Pencil size={13} />
        </button>
      )}
    </div>
  );
}

// ─── tab button ──────────────────────────────────────────────────────────────
function Tab({ label, active, onClick, isDark }) {
  return (
    <button onClick={onClick} className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
      active
        ? 'border-purple-500 text-purple-400'
        : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
    }`}>{label}</button>
  );
}

// Single source of truth for case progress stages — used by the Case Progress tab and the Case Timeline status dropdown
const CASE_PROGRESS_STAGES = [
  { stage: null, percent: 0, label: 'Not started', desc: 'No milestone reached yet' },
  { stage: 'documents_confirmed', percent: 15, label: 'Documents confirmed', desc: 'Client uploaded documents and the agent confirmed them' },
  { stage: 'funds_located', percent: 50, label: 'Funds located', desc: 'Amount and location of the funds identified' },
  { stage: 'payout_in_progress', percent: 75, label: 'Payout in progress', desc: 'Funds are on the way to the client’s payment details' },
  { stage: 'completed', percent: 100, label: 'Completed — case closed', desc: 'Funds arrived on the client’s bank account' },
];
// Timeline status options: the shared progress stages plus generic in-between statuses
const TIMELINE_STATUS_OPTIONS = ['In Progress', ...CASE_PROGRESS_STAGES.filter(o => o.stage).map(o => o.label)];

// ─── DateTimePicker ───────────────────────────────────────────────────────────
const DTP_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DTP_DAYS   = ['Mo','Tu','We','Th','Fr','Sa','Su'];
const DTP_HOURS  = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const DTP_MINS   = ['00','05','10','15','20','25','30','35','40','45','50','55'];

function dtpGrid(year, month) {
  const first = new Date(year, month, 1);
  let dow = first.getDay(); // 0=Sun
  dow = dow === 0 ? 6 : dow - 1; // Mon=0
  const days = [];
  for (let i = dow - 1; i >= 0; i--) days.push(new Date(year, month, -i));
  const last = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= last; d++) days.push(new Date(year, month, d));
  while (days.length < 42) {
    const prev = days[days.length - 1];
    days.push(new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 1));
  }
  return days;
}

function DateTimePicker({ date, time, onChangeDT, isDark, placeholder = 'Select date & time' }) {
  const [open, setOpen]         = useState(false);
  const [step, setStep]         = useState('date'); // 'date' | 'time'
  const [vy, setVy]             = useState(() => date ? parseInt(date.slice(0,4)) : new Date().getFullYear());
  const [vm, setVm]             = useState(() => date ? parseInt(date.slice(5,7)) - 1 : new Date().getMonth());
  const [selHour, setSelHour]   = useState(() => time ? time.slice(0,2) : null);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const prevMonth = () => { if (vm === 0) { setVm(11); setVy(y => y - 1); } else setVm(m => m - 1); };
  const nextMonth = () => { if (vm === 11) { setVm(0); setVy(y => y + 1); } else setVm(m => m + 1); };

  const pickDay = (d) => {
    const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    onChangeDT(iso, time || '');
    setSelHour(null);
    setStep('time');
  };

  const pickTime = (h, m) => {
    const t = `${h}:${m}`;
    onChangeDT(date, t);
    setOpen(false);
    setStep('date');
  };

  const today = new Date();
  const grid  = dtpGrid(vy, vm);

  const displayVal = () => {
    if (!date) return '';
    const [y, mo, d] = date.split('-');
    const monthName = DTP_MONTHS[parseInt(mo)-1]?.slice(0,3);
    const t = time ? ` ${time}` : '';
    return `${d} ${monthName} ${y}${t}`;
  };

  const bg    = isDark ? 'bg-[#0f1729] border-gray-700' : 'bg-white border-gray-200';
  const bgHov = isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100';
  const txt   = isDark ? 'text-white' : 'text-gray-900';
  const sub   = isDark ? 'text-gray-500' : 'text-gray-400';
  const inCls = `w-full px-3 py-2 border rounded-lg text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`;

  return (
    <div className="relative" ref={wrapRef}>
      {/* Trigger input */}
      <div
        className={inCls + (date ? '' : ` ${sub}`)}
        onClick={() => { setOpen(o => !o); setStep('date'); }}
        style={{ userSelect: 'none' }}
      >
        {displayVal() || <span className={sub}>{placeholder}</span>}
      </div>

      {/* Popup */}
      {open && (
        <div className={`absolute z-50 mt-1 rounded-xl border shadow-2xl ${bg} overflow-hidden`} style={{ minWidth: 280 }}>

          {/* ── DATE STEP ── */}
          {step === 'date' && (
            <div className="p-3">
              {/* Month nav */}
              <div className="flex items-center justify-between mb-3">
                <button onClick={prevMonth} className={`p-1 rounded ${bgHov} ${txt}`}><ChevronDown size={14} className="rotate-90" /></button>
                <span className={`text-sm font-semibold ${txt}`}>{DTP_MONTHS[vm]} {vy}</span>
                <button onClick={nextMonth} className={`p-1 rounded ${bgHov} ${txt}`}><ChevronDown size={14} className="-rotate-90" /></button>
              </div>
              {/* Day names */}
              <div className="grid grid-cols-7 mb-1">
                {DTP_DAYS.map(d => (
                  <div key={d} className={`text-center text-xs font-medium py-1 ${sub}`}>{d}</div>
                ))}
              </div>
              {/* Day grid */}
              <div className="grid grid-cols-7 gap-0.5">
                {grid.map((d, i) => {
                  const inMonth  = d.getMonth() === vm;
                  const isToday  = d.toDateString() === today.toDateString();
                  const selected = date === `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                  return (
                    <button
                      key={i}
                      onClick={() => pickDay(d)}
                      className={`
                        w-8 h-8 mx-auto flex items-center justify-center rounded-full text-xs transition-colors
                        ${!inMonth ? 'opacity-25' : ''}
                        ${selected ? 'bg-purple-600 text-white font-bold' : isToday ? `ring-2 ring-purple-500 ${txt} ${bgHov}` : `${txt} ${bgHov}`}
                      `}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── TIME STEP ── */}
          {step === 'time' && (
            <div className="p-3">
              <div className="flex items-center gap-2 mb-3">
                <button onClick={() => setStep('date')} className={`p-1 rounded ${bgHov} ${txt}`}><ChevronDown size={14} className="rotate-90" /></button>
                <span className={`text-sm font-semibold ${txt}`}>
                  {date ? (() => { const [y,mo,d] = date.split('-'); return `${d} ${DTP_MONTHS[parseInt(mo)-1]?.slice(0,3)} ${y}`; })() : ''}
                </span>
                <span className={`ml-auto text-xs ${sub}`}>pick time</span>
              </div>

              {!selHour ? (
                /* Hour grid */
                <div>
                  <p className={`text-xs mb-2 ${sub}`}>Hour</p>
                  <div className="grid grid-cols-6 gap-1">
                    {DTP_HOURS.map(h => (
                      <button
                        key={h}
                        onClick={() => setSelHour(h)}
                        className={`py-1.5 rounded text-xs font-mono transition-colors ${
                          time?.slice(0,2) === h
                            ? 'bg-purple-600 text-white'
                            : `${txt} ${bgHov}`
                        }`}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                /* Minute grid */
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <button onClick={() => setSelHour(null)} className={`p-1 rounded ${bgHov} ${txt}`}><ChevronDown size={14} className="rotate-90" /></button>
                    <p className={`text-xs ${sub}`}>{selHour}:__ — pick minute</p>
                  </div>
                  <div className="grid grid-cols-6 gap-1">
                    {DTP_MINS.map(m => (
                      <button
                        key={m}
                        onClick={() => pickTime(selHour, m)}
                        className={`py-1.5 rounded text-xs font-mono transition-colors ${
                          time === `${selHour}:${m}`
                            ? 'bg-purple-600 text-white'
                            : `${txt} ${bgHov}`
                        }`}
                      >
                        :{m}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── modal wrapper ────────────────────────────────────────────────────────────
function Modal({ title, onClose, isDark, children, maxW = 'max-w-md' }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className={`w-full ${maxW} rounded-lg shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
          <button onClick={onClose} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}><X size={20} /></button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

// ─── standalone helpers ───────────────────────────────────────────────────────
const calcAge = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
};

const timeAgoStr = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d !== 1 ? 's' : ''} ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} month${mo !== 1 ? 's' : ''} ago`;
  const yr = Math.floor(mo / 12);
  return `${yr} year${yr !== 1 ? 's' : ''} ago`;
};

const fmtDT = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', hour12: false });
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isDark = theme === 'dark';

  const [client, setClient]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('comments');

  // Comments
  const [comments, setComments]     = useState([]);
  const [showNewComment, setShowNewComment] = useState(false);
  const [newComment, setNewComment] = useState({ title: '', body: '', pinned: false, status: '', next_call_date: '', next_call_time: '' });
  const [statusOptions, setStatusOptions] = useState([]);

  // Edit comment modal
  const [editingComment, setEditingComment] = useState(null); // comment object being edited
  const [editCommentForm, setEditCommentForm] = useState({ title: '', body: '', pinned: false, status: '', next_call_date: '', next_call_time: '' });

  // Transactions (per-client)
  const [clientTxns, setClientTxns] = useState([]);
  const [showNewTxn, setShowNewTxn] = useState(false);
  const [newTxn, setNewTxn] = useState({ amount: '', type: 'deposit', currency: 'USD', status: 'completed', description: '' });

  // Portfolios
  const [portfolios, setPortfolios] = useState([]);
  const [showNewPortfolio, setShowNewPortfolio] = useState(false);
  const [editPortfolio, setEditPortfolio] = useState(null);
  const [portfolioForm, setPortfolioForm] = useState({ company: '', dateFrom: '', dateTo: '', industry: '', funding: 0, overallBalance: 0, withdrawals: 0 });

  // Chat
  const [messages, setMessages]   = useState([]);
  const [msgInput, setMsgInput]   = useState('');

  // Case Management
  const CRYPTO_PORTAL_KEYS = ['cryptocurrency', 'blockchainLink', 'amount', 'source', 'foundAt'];
  const BANK_PORTAL_KEYS = ['bank', 'currency', 'amount', 'account', 'source', 'foundAt'];
  const [caseManagement, setCaseManagement] = useState({
    cryptocurrency: '', blockchainLink: '', cryptoAmount: '', cryptoSource: '', cryptoFoundAt: '',
    cryptoShowToClient: false, cryptoVisibleFields: null,
    bank: '', bankCurrency: '', bankAmount: '', bankAccount: '', bankSource: '', bankFoundAt: '',
    bankShowToClient: false, bankVisibleFields: null,
  });
  const [caseAssets, setCaseAssets] = useState([]);
  const [editingCaseAsset, setEditingCaseAsset] = useState(null);
  const [cryptoPrices, setCryptoPrices] = useState({});
  const [nextStep, setNextStep] = useState({ title: '', description: '', actionLabel: '', showToClient: true });
  const [blocker, setBlocker] = useState({ text: '', waitingOn: '', eta: '', showToClient: true });
  const [caseStage, setCaseStage] = useState(null);
  const [caseStageSaving, setCaseStageSaving] = useState(false);
  const [holdForm, setHoldForm] = useState({ open: false, reason: '', waitingOn: 'third_party', eta: '' });
  const [holdBusy, setHoldBusy] = useState(false);
  const isCaseFieldVisible = (block, portalKey) => {
    const arr = caseManagement[block === 'crypto' ? 'cryptoVisibleFields' : 'bankVisibleFields'];
    return arr == null || arr.includes(portalKey);
  };
  const toggleCaseFieldVisibility = (block, portalKey) => {
    const stateKey = block === 'crypto' ? 'cryptoVisibleFields' : 'bankVisibleFields';
    const all = block === 'crypto' ? CRYPTO_PORTAL_KEYS : BANK_PORTAL_KEYS;
    setCaseManagement(p => {
      const current = p[stateKey] == null ? [...all] : [...p[stateKey]];
      const next = current.includes(portalKey) ? current.filter(k => k !== portalKey) : [...current, portalKey];
      return { ...p, [stateKey]: next };
    });
  };

  // Case Timeline
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineForm, setTimelineForm] = useState({ source: '', caseStatus: '', eventDate: '', comment: '', showToClient: false });
  const [editingTimelineEvent, setEditingTimelineEvent] = useState(null);
  const [timelineExpanded, setTimelineExpanded] = useState(true);

  // Portal credentials
  const [portalCreds, setPortalCreds] = useState({ login: null, hasPassword: false });
  const [portalCredsLoading, setPortalCredsLoading] = useState(false);
  const [cabinetTab, setCabinetTab] = useState('portalAccess');
  const chatEndRef = useRef(null);

  // Change status modal
  const [showChangeStatus, setShowChangeStatus] = useState(false);

  // Actions dropdown
  const [showActions, setShowActions] = useState(false);
  const actionsRef = useRef(null);
  const fieldsConfigRef = useRef(null);

  // Action modals
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ newPassword: '', confirmPassword: '', forceLogout: false });
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicForm, setMagicForm] = useState({ expires: 30, oneTime: false, revokePrev: true });
  const [generatedLink, setGeneratedLink] = useState('');
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferUsers, setTransferUsers] = useState([]);

  // Pinned comments
  const [pinnedComments, setPinnedComments] = useState([]);

  // Configurable Personal Information fields visibility
  const PI_FIELDS = [
    'Date of birth', 'Gender', 'Phone', 'Email', 'Country', 'Language',
    'City', 'Address', 'Affiliate', 'Affiliate ID', 'Source', 'Referral', 'Funnel', 'Reg. IP',
  ];
  const [showFieldsConfig, setShowFieldsConfig] = useState(false);
  const [visibleFields, setVisibleFields] = useState(() => new Set(PI_FIELDS));
  useEffect(() => {
    let cancelled = false;
    userSettingsService.get('clientDetail.visibleFields', null).then(value => {
      if (!cancelled && Array.isArray(value)) setVisibleFields(new Set(value));
    });
    return () => { cancelled = true; };
  }, []);
  const toggleFieldVisibility = (f) => {
    setVisibleFields(prev => {
      const next = new Set(prev);
      next.has(f) ? next.delete(f) : next.add(f);
      userSettingsService.set('clientDetail.visibleFields', [...next]).catch(() => {});
      return next;
    });
  };

  // Documents sub-tab
  const [docSubTab, setDocSubTab] = useState('verification');
  const DOC_TYPES = ['Passport', 'Driver License', 'ID Front', 'ID Back', 'Utility Bill', 'Bank Statement'];
  const [clientDocs, setClientDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploadDocType, setUploadDocType] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docFileRef = useRef(null);
  const invoiceFileRef = useRef(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const filesFileRef = useRef(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const cls = {
    card: `rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`,
    input: `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`,
    label: `block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`,
    btn: `px-4 py-2 text-sm border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`,
    btnPrimary: 'px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50',
  };


  // ── Load data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    logActivity('view', 'client', id, `Opened client #${id}`);
    loadStatusColors();
    loadClient();
    api.get('/options/statuses').then(({ data }) => setStatusOptions(data.statuses || [])).catch(() => {});
    api.get(`/clients/${id}/comments`).then(({ data }) => {
      setPinnedComments((data || []).filter(c => c.pinned).slice(0, 5));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchCryptoUsdPrices().then(setCryptoPrices).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'comments')      loadComments();
    if (activeTab === 'transactions')  loadClientTxns();
    if (activeTab === 'caseInfo')      loadPortfolios();
    if (activeTab === 'chats')         loadMessages();
    if (activeTab === 'documents')     loadClientDocs();
    if (activeTab === 'client')        { loadCaseManagement(); loadTimeline(); loadPortalCreds(); }
    logActivity('view', `client_tab_${activeTab}`, id, `Viewed tab "${activeTab}" on client #${id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id]);

  useEffect(() => {
    if (actionsRef.current) {
      const h = (e) => { if (!actionsRef.current?.contains(e.target)) setShowActions(false); };
      document.addEventListener('mousedown', h);
      return () => document.removeEventListener('mousedown', h);
    }
  }, []);

  useEffect(() => {
    if (!showFieldsConfig) return;
    const h = (e) => { if (fieldsConfigRef.current && !fieldsConfigRef.current.contains(e.target)) setShowFieldsConfig(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showFieldsConfig]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadClient = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/clients/${id}`);
      setClient(data);
    } catch { toast.error('Failed to load client'); }
    finally { setLoading(false); }
  };

  const loadComments = async () => { try { const { data } = await api.get(`/clients/${id}/comments`); setComments(data); } catch {} };
  const loadClientTxns = async () => { try { const { data } = await api.get('/transactions', { params: { client_id: id, limit: 100 } }); setClientTxns(data.transactions || []); } catch {} };
  const loadPortfolios= async () => { try { const { data } = await api.get(`/clients/${id}/portfolios`); setPortfolios(data); } catch {} };
  const loadMessages  = async () => { try { const { data } = await api.get(`/clients/${id}/messages`); setMessages(data); } catch {} };
  const startTelephonyCall = () => {
    const number = normalizeDialNumber(client?.phone || client?.mobile);
    if (!number) {
      toast.error('Client phone number is missing');
      return;
    }
    // Hand the number to the active local softphone (MicroSIP, etc.).
    // The operator adds any prefix and picks the provider in the softphone.
    openLocalSipDialer(number);
    toast.success(t('clientDetail.openingSoftphone'));
  };

  const loadCaseManagement = async () => {
    try {
      const { data } = await api.get(`/clients/${id}/case-management`);
      if (data && Object.keys(data).length > 0) {
        const { nextStep: loadedNextStep, blocker: loadedBlocker, stage: loadedStage, progress: _ignored, ...caseData } = data;
        setCaseManagement(prev => ({ ...prev, ...caseData }));
        setCaseStage(loadedStage || null);
        if (loadedNextStep) {
          setNextStep({
            title: loadedNextStep.title || '',
            description: loadedNextStep.description || '',
            actionLabel: loadedNextStep.actionLabel || '',
            showToClient: loadedNextStep.showToClient !== false,
          });
        }
        if (loadedBlocker) {
          setBlocker({
            text: loadedBlocker.text || '',
            waitingOn: loadedBlocker.waitingOn || '',
            eta: loadedBlocker.eta || '',
            showToClient: loadedBlocker.showToClient !== false,
          });
        }
      }
    } catch {}
    try {
      const { data } = await api.get(`/clients/${id}/case-assets`);
      setCaseAssets(data || []);
    } catch {}
  };
  const resetCaseManagementForm = () => {
    setEditingCaseAsset(null);
    setCaseManagement({
      cryptocurrency: '', blockchainLink: '', cryptoAmount: '', cryptoSource: '', cryptoFoundAt: '',
      cryptoShowToClient: false, cryptoVisibleFields: null,
      bank: '', bankCurrency: '', bankAmount: '', bankAccount: '', bankSource: '', bankFoundAt: '',
      bankShowToClient: false, bankVisibleFields: null,
    });
  };
  const editCaseAsset = (asset) => {
    setEditingCaseAsset(asset);
    if (asset.assetType === 'crypto') {
      setCaseManagement({
        cryptocurrency: asset.cryptocurrency || '',
        blockchainLink: asset.blockchainLink || '',
        cryptoAmount: asset.amount ?? '',
        cryptoSource: asset.source || '',
        cryptoFoundAt: asset.foundAt || '',
        cryptoShowToClient: !!asset.showToClient,
        cryptoVisibleFields: asset.visibleFields,
        bank: '', bankCurrency: '', bankAmount: '', bankAccount: '', bankSource: '', bankFoundAt: '',
        bankShowToClient: false, bankVisibleFields: null,
      });
    } else {
      setCaseManagement({
        cryptocurrency: '', blockchainLink: '', cryptoAmount: '', cryptoSource: '', cryptoFoundAt: '',
        cryptoShowToClient: false, cryptoVisibleFields: null,
        bank: asset.bank || '',
        bankCurrency: asset.bankCurrency || '',
        bankAmount: asset.amount ?? '',
        bankAccount: asset.bankAccount || '',
        bankSource: asset.source || '',
        bankFoundAt: asset.foundAt || '',
        bankShowToClient: !!asset.showToClient,
        bankVisibleFields: asset.visibleFields,
      });
    }
  };
  const saveCaseManagement = async () => {
    const cryptoFilled = caseManagement.cryptocurrency || caseManagement.cryptoAmount || caseManagement.cryptoSource || caseManagement.blockchainLink || caseManagement.cryptoFoundAt;
    const bankFilled = caseManagement.bank || caseManagement.bankAmount || caseManagement.bankAccount || caseManagement.bankSource || caseManagement.bankCurrency || caseManagement.bankFoundAt;
    if (!cryptoFilled && !bankFilled) { toast.error('Fill at least one section'); return; }
    try {
      if (editingCaseAsset) {
        const isCrypto = editingCaseAsset.assetType === 'crypto';
        if (isCrypto && !cryptoFilled) { toast.error('Fill cryptocurrency section'); return; }
        if (!isCrypto && !bankFilled) { toast.error('Fill bank section'); return; }
        await api.put(`/clients/${id}/case-assets/${editingCaseAsset.id}`, isCrypto ? {
          asset_type: 'crypto',
          cryptocurrency: caseManagement.cryptocurrency || null,
          blockchain_link: caseManagement.blockchainLink || null,
          amount: caseManagement.cryptoAmount ? parseFloat(caseManagement.cryptoAmount) : null,
          source: caseManagement.cryptoSource || null,
          found_at: caseManagement.cryptoFoundAt || null,
          show_to_client: caseManagement.cryptoShowToClient,
          visible_fields: caseManagement.cryptoVisibleFields,
        } : {
          asset_type: 'bank',
          bank: caseManagement.bank || null,
          bank_currency: caseManagement.bankCurrency || null,
          amount: caseManagement.bankAmount ? parseFloat(caseManagement.bankAmount) : null,
          bank_account: caseManagement.bankAccount || null,
          source: caseManagement.bankSource || null,
          found_at: caseManagement.bankFoundAt || null,
          show_to_client: caseManagement.bankShowToClient,
          visible_fields: caseManagement.bankVisibleFields,
        });
      } else {
        if (cryptoFilled) {
          await api.post(`/clients/${id}/case-assets`, {
            asset_type: 'crypto',
            cryptocurrency: caseManagement.cryptocurrency || null,
            blockchain_link: caseManagement.blockchainLink || null,
            amount: caseManagement.cryptoAmount ? parseFloat(caseManagement.cryptoAmount) : null,
            source: caseManagement.cryptoSource || null,
            found_at: caseManagement.cryptoFoundAt || null,
            show_to_client: caseManagement.cryptoShowToClient,
            visible_fields: caseManagement.cryptoVisibleFields,
          });
        }
        if (bankFilled) {
          await api.post(`/clients/${id}/case-assets`, {
            asset_type: 'bank',
            bank: caseManagement.bank || null,
            bank_currency: caseManagement.bankCurrency || null,
            amount: caseManagement.bankAmount ? parseFloat(caseManagement.bankAmount) : null,
            bank_account: caseManagement.bankAccount || null,
            source: caseManagement.bankSource || null,
            found_at: caseManagement.bankFoundAt || null,
            show_to_client: caseManagement.bankShowToClient,
            visible_fields: caseManagement.bankVisibleFields,
          });
        }
      }
      toast.success(editingCaseAsset ? 'Case updated' : 'Case saved');
      resetCaseManagementForm();
      loadCaseManagement();
    } catch { toast.error('Save failed'); }
  };
  const toggleAssetVisibility = async (assetId) => {
    try { await api.patch(`/clients/${id}/case-assets/${assetId}/visibility`); loadCaseManagement(); }
    catch { toast.error('Failed'); }
  };
  const deleteCaseAsset = async (assetId) => {
    if (!window.confirm('Delete this case entry?')) return;
    try { await api.delete(`/clients/${id}/case-assets/${assetId}`); loadCaseManagement(); }
    catch { toast.error('Delete failed'); }
  };
  const saveNextStep = async () => {
    try {
      const { data } = await api.put(`/clients/${id}/case-management/next-step`, {
        title: nextStep.title || null,
        description: nextStep.description || null,
        action_label: nextStep.actionLabel || null,
        show_to_client: nextStep.showToClient,
      });
      setNextStep({
        title: data.title || '',
        description: data.description || '',
        actionLabel: data.actionLabel || '',
        showToClient: data.showToClient !== false,
      });
      toast.success('Next step saved');
    } catch {
      toast.error('Save failed');
    }
  };
  const saveBlocker = async () => {
    try {
      const { data } = await api.put(`/clients/${id}/case-management/blocker`, {
        text: blocker.text || null,
        waiting_on: blocker.waitingOn || null,
        eta: blocker.eta || null,
        show_to_client: blocker.showToClient,
      });
      setBlocker({
        text: data.text || '',
        waitingOn: data.waitingOn || '',
        eta: data.eta || '',
        showToClient: data.showToClient !== false,
      });
      toast.success('Blocker saved');
    } catch {
      toast.error('Save failed');
    }
  };
  const saveCaseStage = async (stage) => {
    setCaseStageSaving(true);
    try {
      const { data } = await api.put(`/clients/${id}/case-management/stage`, { stage });
      setCaseStage(data.stage || null);
      toast.success('Case progress updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setCaseStageSaving(false); }
  };
  const setCaseHold = async () => {
    if (!holdForm.reason.trim()) { toast.error('Hold reason is required'); return; }
    setHoldBusy(true);
    try {
      const { data } = await api.post(`/clients/${id}/case-management/hold`, {
        reason: holdForm.reason.trim(),
        waiting_on: holdForm.waitingOn || null,
        eta: holdForm.eta || null,
      });
      setBlocker({
        text: data.text || '',
        waitingOn: data.waitingOn || '',
        eta: data.eta || '',
        showToClient: data.showToClient !== false,
      });
      setHoldForm({ open: false, reason: '', waitingOn: 'third_party', eta: '' });
      loadTimeline();
      setCabinetTab('nextStep');
      toast.success('Case put on hold — now describe the next step to lift it');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setHoldBusy(false); }
  };
  const releaseCaseHold = async () => {
    setHoldBusy(true);
    try {
      await api.post(`/clients/${id}/case-management/hold/release`, {});
      setBlocker({ text: '', waitingOn: '', eta: '', showToClient: true });
      loadTimeline();
      toast.success('Hold released');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setHoldBusy(false); }
  };

  const loadTimeline = async () => {
    try { const { data } = await api.get(`/clients/${id}/timeline`); setTimelineEvents(data); } catch {}
  };
  const resetTimelineForm = () => {
    setEditingTimelineEvent(null);
    setTimelineForm({ source: '', caseStatus: '', eventDate: '', comment: '', showToClient: false });
  };
  const editTimelineEvent = (event) => {
    setEditingTimelineEvent(event);
    setTimelineForm({
      source: event.source || '',
      caseStatus: event.caseStatus || '',
      eventDate: event.eventDate || '',
      comment: event.comment || '',
      showToClient: !!event.showToClient,
    });
  };
  const addTimelineEvent = async () => {
    if (!timelineForm.caseStatus) { toast.error('Case Status is required'); return; }
    if (!timelineForm.eventDate)  { toast.error('Date is required'); return; }
    try {
      const payload = {
        source: timelineForm.source || null,
        case_status: timelineForm.caseStatus,
        event_date: timelineForm.eventDate,
        comment: timelineForm.comment || null,
        show_to_client: timelineForm.showToClient,
      };
      if (editingTimelineEvent) {
        const { data } = await api.put(`/clients/${id}/timeline/${editingTimelineEvent.id}`, payload);
        setTimelineEvents(prev => prev.map(e => e.id === data.id ? data : e));
        toast.success('Event updated');
      } else {
        const { data } = await api.post(`/clients/${id}/timeline`, payload);
        setTimelineEvents(prev => [data, ...prev]);
        toast.success('Event added');
      }
      resetTimelineForm();
    } catch { toast.error('Failed to add event'); }
  };
  const deleteTimelineEvent = async (eventId) => {
    try {
      await api.delete(`/clients/${id}/timeline/${eventId}`);
      setTimelineEvents(prev => prev.filter(e => e.id !== eventId));
      toast.success('Event deleted');
    } catch { toast.error('Delete failed'); }
  };

  const loadPortalCreds = async () => {
    try { const { data } = await api.get(`/clients/${id}/portal-credentials`); setPortalCreds(data); } catch {}
  };
  const generatePortalCreds = async () => {
    setPortalCredsLoading(true);
    try {
      const { data } = await api.post(`/clients/${id}/portal-credentials`);
      setPortalCreds({ login: data.login, password: data.password, hasPassword: true });
      toast.success('Portal credentials generated');
    } catch { toast.error('Failed'); } finally { setPortalCredsLoading(false); }
  };
  const loadClientDocs = async () => {
    try {
      setDocsLoading(true);
      const { data } = await api.get('/documents', { params: { client_id: id, limit: 100 } });
      setClientDocs(data.documents || []);
    } catch {} finally { setDocsLoading(false); }
  };
  const handleDocUpload = async (file, docType) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      setUploadingDoc(true);
      const { data } = await api.post('/documents/upload', fd, {
        params: { client_id: id, doc_type: docType },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const isVerification = DOC_TYPES.includes(docType);
      toast.success(isVerification
        ? `${docType} загружен, одобрен и опубликован клиенту`
        : `${docType} загружен — проверьте превью`);
      loadClientDocs();
      // Don't open preview modal for verification slot uploads — already approved+published
      if (data?.id && !isVerification) setPreviewDoc(data);
    } catch { toast.error('Upload failed'); } finally { setUploadingDoc(false); setUploadDocType(null); }
  };
  const changeDocStatus = async (docId, newStatus) => {
    try {
      await api.patch(`/documents/${docId}/status`, { status: newStatus });
      toast.success(newStatus === 'declined' ? 'Document declined and deleted' : `Status -> ${newStatus}`);
      loadClientDocs();
    } catch { toast.error('Failed'); }
  };
  const deleteClientDoc = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Deleted');
      loadClientDocs();
    } catch { toast.error('Delete failed'); }
  };
  const handleInvoiceUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      setUploadingInvoice(true);
      const { data } = await api.post('/documents/upload', fd, {
        params: { client_id: id, doc_type: 'invoice' },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Инвойс загружен — проверьте превью');
      loadClientDocs();
      if (data?.id) setPreviewDoc(data);
    } catch { toast.error('Upload failed'); } finally { setUploadingInvoice(false); }
  };
  const handleFilesUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      setUploadingFiles(true);
      const { data } = await api.post('/documents/upload', fd, {
        params: { client_id: id, doc_type: 'file' },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Файл загружен — проверьте превью');
      loadClientDocs();
      if (data?.id) setPreviewDoc(data);
    } catch { toast.error('Upload failed'); } finally { setUploadingFiles(false); }
  };

  // ── Field save ─────────────────────────────────────────────────────────────
  const handleFieldSave = async (field, value, silent = false) => {
    try {
      await api.patch(`/clients/${id}/field`, { field, value });
      setClient(c => ({ ...c, [field]: value }));
      if (!silent) toast.success('Saved');
    } catch { toast.error('Save failed'); }
  };

  // ── Comments ───────────────────────────────────────────────────────────────
  const submitComment = async () => {
    if (!newComment.title) { toast.error('Topic is required'); return; }
    if (!newComment.body)  { toast.error('Comment is required'); return; }
    if (!newComment.next_call_date) { toast.error('Next Call date is required'); return; }
    try {
      const time = newComment.next_call_time || '10:00';
      // Convert local picker selection to UTC so the server stores and compares UTC correctly
      const nextCallAt = new Date(`${newComment.next_call_date}T${time}:00`).toISOString().slice(0, 19);
      await api.post(`/clients/${id}/comments`, {
        title: newComment.title,
        body: newComment.body,
        pinned: newComment.pinned,
        nextCallAt,
      });
      toast.success('Comment saved & call scheduled');
      setShowNewComment(false);
      setNewComment({ title: '', body: '', pinned: false, next_call_date: '', next_call_time: '' });
      loadComments();
      api.get(`/clients/${id}/comments`).then(({ data }) => {
        setPinnedComments((data || []).filter(c => c.pinned).slice(0, 5));
      }).catch(() => {});
    } catch { toast.error('Failed to add comment'); }
  };

  const deleteComment = async (cid) => {
    try {
      await api.delete(`/clients/${id}/comments/${cid}`);
      loadComments();
      api.get(`/clients/${id}/comments`).then(({ data }) => {
        setPinnedComments((data || []).filter(c => c.pinned).slice(0, 5));
      }).catch(() => {});
      toast.success('Deleted');
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const openEditComment = (c) => {
    const dt = c.nextCallAt ? new Date(c.nextCallAt) : null;
    setEditCommentForm({
      title:          c.title || '',
      body:           c.body  || '',
      pinned:         c.pinned || false,
      next_call_date: dt ? dt.toISOString().slice(0, 10) : '',
      next_call_time: dt ? `${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}` : '',
    });
    setEditingComment(c);
  };

  const submitEditComment = async () => {
    if (!editCommentForm.title) { toast.error('Topic is required'); return; }
    if (!editCommentForm.body)  { toast.error('Comment is required'); return; }
    try {
      const time = editCommentForm.next_call_time || '10:00';
      const nextCallAt = editCommentForm.next_call_date
        ? new Date(`${editCommentForm.next_call_date}T${time}:00`).toISOString().slice(0, 19)
        : null;
      await api.patch(`/clients/${id}/comments/${editingComment.id}`, {
        title:        editCommentForm.title,
        body:         editCommentForm.body,
        pinned:       editCommentForm.pinned,
        nextCallAt,
      });
      toast.success('Comment updated');
      setEditingComment(null);
      loadComments();
      // refresh pinned notes
      api.get(`/clients/${id}/comments`).then(({ data }) => {
        setPinnedComments((data || []).filter(c => c.pinned).slice(0, 5));
      }).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  // ── Transactions (per-client) ──────────────────────────────────────────────
  const submitNewTxn = async () => {
    const amount = parseFloat(String(newTxn.amount).replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) { toast.error('Amount required'); return; }
    try {
      const canUseAdminTxnControls = canUseTransactionAdminControls(userRole);
      await api.post('/transactions', {
        clientId: String(id),
        type: canUseAdminTxnControls ? newTxn.type : 'deposit',
        amount,
        currency: 'USD',
        status: canUseAdminTxnControls ? newTxn.status : 'completed',
        description: newTxn.description || null,
      });
      toast.success('Transaction created');
      setShowNewTxn(false);
      setNewTxn({ amount: '', type: 'deposit', currency: 'USD', status: 'completed', description: '' });
      loadClientTxns();
    } catch { toast.error('Failed to create transaction'); }
  };

  // ── Portfolios ─────────────────────────────────────────────────────────────
  const submitPortfolio = async () => {
    try {
      if (editPortfolio) {
        await api.put(`/clients/${id}/portfolios/${editPortfolio.id}`, portfolioForm);
        toast.success('Portfolio updated');
      } else {
        await api.post(`/clients/${id}/portfolios`, portfolioForm);
        toast.success('Portfolio added');
      }
      setShowNewPortfolio(false); setEditPortfolio(null);
      setPortfolioForm({ company: '', dateFrom: '', dateTo: '', industry: '', funding: 0, overallBalance: 0, withdrawals: 0 });
      loadPortfolios();
    } catch { toast.error('Failed to save portfolio'); }
  };

  const deletePortfolio = async (pid) => {
    try { await api.delete(`/clients/${id}/portfolios/${pid}`); loadPortfolios(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  // ── Chat ───────────────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!msgInput.trim()) return;
    try {
      const { data } = await api.post(`/clients/${id}/messages`, { body: msgInput.trim() });
      setMessages(m => [...m, data]);
      setMsgInput('');
    } catch { toast.error('Failed to send'); }
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleChangePassword = async () => {
    if (pwForm.newPassword !== pwForm.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (!pwForm.newPassword) { toast.error('Password required'); return; }
    try {
      await api.post(`/clients/${id}/change-password`, { new_password: pwForm.newPassword, force_logout: pwForm.forceLogout });
      toast.success('Password changed');
      setShowChangePassword(false); setPwForm({ newPassword: '', confirmPassword: '', forceLogout: false });
    } catch { toast.error('Failed to change password'); }
  };

  const handleGenerateMagicLink = async () => {
    try {
      const { data } = await api.post(`/clients/${id}/magic-link`, {
        expires_minutes: magicForm.expires,
        one_time_use: magicForm.oneTime,
        revoke_previous: magicForm.revokePrev,
      });
      setGeneratedLink(data.link);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail) toast.error(detail);
      else if (!e?.response) toast.error('Network error — backend unreachable');
      else toast.error(`Failed (${e.response.status})`);
    }
  };

  const handleBlockToggle = async () => {
    try {
      const endpoint = client.active ? `/clients/${id}/block` : `/clients/${id}/unblock`;
      await api.post(endpoint);
      toast.success(client.active ? 'Client blocked' : 'Client unblocked');
      setShowBlockConfirm(false);
      loadClient();
    } catch { toast.error('Action failed'); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className={`flex items-center justify-center h-screen ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('clientDetail.loading')}</div>
  );
  if (!client) return (
    <div className="flex items-center justify-center h-screen text-red-400">{t('clientDetail.clientNotFound')}</div>
  );

  const TABS = ['comments', 'caseInfo', 'transactions', 'client', 'documents', 'chats', 'personalInfo'];
  const TAB_LABELS = {
    comments: t('clientDetail.comments'),
    caseInfo: t('clientDetail.caseInfo'),
    transactions: t('clientDetail.transactions'),
    client: t('clientDetail.clientCabinet'),
    documents: t('clientDetail.documents'),
    chats: t('clientDetail.chats'),
    personalInfo: t('clientDetail.personalInfo'),
  };
  const FIELD_LABELS = {
    'Date of birth': t('clientDetail.dateOfBirth'),
    Gender: t('clientDetail.gender'),
    Phone: t('clientDetail.phone'),
    Email: t('clientDetail.email'),
    Country: t('clientDetail.country'),
    Language: t('clientDetail.language'),
    City: t('clientDetail.city'),
    Address: t('clientDetail.address'),
    Affiliate: t('clientDetail.affiliate'),
    'Affiliate ID': t('clientDetail.affiliateId'),
    Source: t('clientDetail.source'),
    Referral: t('clientDetail.referral'),
    Funnel: t('clientDetail.funnel'),
    'Reg. IP': t('clientDetail.regIp'),
  };

  const age      = calcAge(client.birthdate || client.birth_date);
  const fullName = [client.firstName, client.lastName].filter(Boolean).join(' ') || '—';

  const dept = (client.department || '').toLowerCase();

  // Current user role from the backend auth context
  const currentUser  = getCurrentUser();
  const userRole     = currentUser.role || '';
  const canUseAdminTxnControls = canUseTransactionAdminControls(userRole);
  const _canEditSales = canEditSales(userRole);
  const _canEditRet   = canEditRetention(userRole);
  const portalCaseAssets = caseAssets.filter(a => a.showToClient);
  const internalCaseAssets = caseAssets.filter(a => !a.showToClient);
  const portalTimelineEvents = timelineEvents.filter(e => e.showToClient);
  const internalTimelineEvents = timelineEvents.filter(e => !e.showToClient);

  const renderCaseAssetList = (items, title, emptyText) => (
    <div className="space-y-2">
      <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        {title} ({items.length})
      </p>
      {items.length === 0 ? (
        <div className={`px-3 py-2 rounded-md border text-xs ${isDark ? 'border-gray-800 text-gray-500 bg-[#0f1729]' : 'border-gray-200 text-gray-400 bg-white'}`}>{emptyText}</div>
      ) : items.map(a => (
        <div key={a.id} className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-[#0f1729]' : 'border-gray-200 bg-white'}`}>
          {(() => {
            const tokenAmount = a.assetType === 'crypto' ? formatTokenAmount(a.amount, a.cryptocurrency) : null;
            const approxUsd = a.assetType === 'crypto' ? formatApproxUsd(a.amount, a.cryptocurrency, cryptoPrices) : null;
            return (
              <>
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${a.assetType === 'crypto' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}`}>
            {a.assetType}
          </span>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
              {a.assetType === 'crypto'
                ? `${a.cryptocurrency || '-'} · ${tokenAmount}${approxUsd ? ' · ' + approxUsd : ''}${a.source ? ' · ' + a.source : ''}`
                : `${a.bank || '-'} · ${a.amount ?? '-'} ${a.bankCurrency || ''}${a.source ? ' · ' + a.source : ''}`}
            </p>
            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {a.foundAt ? `Found: ${a.foundAt}` : 'No date'}
              {a.visibleFields?.length ? ` · Client fields: ${a.visibleFields.join(', ')}` : a.showToClient ? ' · Client sees all filled fields' : ''}
            </p>
          </div>
          <button onClick={() => toggleAssetVisibility(a.id)}
            className={`px-2 py-0.5 text-xs rounded transition-colors ${a.showToClient ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : (isDark ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200')}`}>
            {a.showToClient ? 'Visible' : 'Hidden'}
          </button>
          <button onClick={() => editCaseAsset(a)} className={cls.btn + ' px-2 py-1'} title="Edit">
            <Pencil size={13} />
          </button>
          <button onClick={() => deleteCaseAsset(a.id)} className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30" title="Delete">
            <Trash2 size={13} />
          </button>
              </>
            );
          })()}
        </div>
      ))}
    </div>
  );

  const renderTimelineList = (items, title, emptyText) => (
    <div className="space-y-2">
      <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        {title} ({items.length})
      </p>
      {items.length === 0 ? (
        <div className={`px-3 py-2 rounded-md border text-xs ${isDark ? 'border-gray-800 text-gray-500 bg-[#0f1729]' : 'border-gray-200 text-gray-400 bg-white'}`}>{emptyText}</div>
      ) : items.map(ev => (
        <div key={ev.id} className={`px-4 py-3 flex gap-3 rounded-lg border ${isDark ? 'border-gray-800 bg-[#0a0e1a]' : 'border-gray-200 bg-white'}`}>
          <div className="flex-shrink-0 mt-0.5">
            <div className={`w-2 h-2 rounded-full mt-1.5 ${ev.showToClient ? 'bg-purple-500' : 'bg-gray-500'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className={`text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{ev.caseStatus}</span>
              {ev.source && <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>· {ev.source}</span>}
              {ev.eventDate && <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>· {ev.eventDate}</span>}
              <span className={`text-xs px-1.5 py-0.5 rounded ${ev.showToClient ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>
                {ev.showToClient ? 'visible to client' : 'internal only'}
              </span>
            </div>
            {ev.comment && <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{ev.comment}</p>}
            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{ev.createdBy} · {fmtDate(ev.createdAt)}</p>
          </div>
          <button onClick={() => editTimelineEvent(ev)} className={cls.btn + ' px-2 py-1 self-start'} title="Edit">
            <Pencil size={13} />
          </button>
          <button onClick={() => deleteTimelineEvent(ev.id)} className="text-red-500 hover:text-red-400 flex-shrink-0 self-start" title="Delete">
            <Trash2 size={14} />
          </button>
        </div>
      ))}
    </div>
  );

  // Card/section shared styles
  const section = `rounded-xl border ${isDark ? 'bg-[#0f1729] border-gray-800/80' : 'bg-white border-gray-200'} shadow-sm`;
  const sectionHd = `px-4 py-2.5 border-b text-[11px] font-bold uppercase tracking-widest ${isDark ? 'border-gray-800/80 text-gray-500' : 'border-gray-100 text-gray-400'}`;
  const infoRow = `flex items-center justify-between gap-2 px-4 py-2 rounded-lg transition-colors cursor-default ${isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-gray-50'}`;
  const infoLabel = `text-[11px] font-medium flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`;
  const infoVal   = `text-[12px] font-medium text-right truncate max-w-[160px] ${isDark ? 'text-gray-200' : 'text-gray-700'}`;

  // Avatar — initials + dept color
  const initials = [client.firstName, client.lastName].filter(Boolean).map(n => n[0]).join('').toUpperCase() || '?';
  const deptAvatarStyle = dept === 'retention'
    ? { background: 'linear-gradient(135deg,#0e7490,#06b6d4)', color: '#fff' }
    : { background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)', color: '#fff' };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0e1a]' : 'bg-gray-100'}`}>

      {/* â•â• 1. TOP HEADER BAR â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div className={`sticky top-0 z-20 px-4 py-3 border-b flex items-center justify-between gap-4 ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`}
        style={{ boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.06)' }}>

        {/* Left: back + avatar + name block */}
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/clients')}
            className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/[0.07]' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}>
            <ArrowLeft size={17} />
          </button>

          {/* Name + meta */}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className={`text-[15px] font-bold truncate leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {fullName}{age ? <span className={`ml-1 font-normal text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>({age})</span> : ''}
              </h1>
              {client.active
                ? <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                    style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.25)' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" style={{ boxShadow: '0 0 5px rgba(74,222,128,0.8)' }} />
                    {t('clientDetail.active')}
                  </span>
                : <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                    style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' }}>
                    {t('clientDetail.blocked')}
                  </span>
              }
            </div>
            <p className={`text-[11px] font-mono mt-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>#{client.id}</p>
          </div>
        </div>

        {/* Right: action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowNewComment(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
            style={{ borderColor: 'rgba(99,102,241,0.4)', color: isDark ? '#a5b4fc' : '#6366f1' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            <Phone size={12} /> {t('clientDetail.addCallback')}
          </button>
          <button
            onClick={() => setShowChangeStatus(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all"
            style={{
              background: 'linear-gradient(135deg, rgba(99,102,241,0.18), rgba(139,92,246,0.12))',
              border: '1px solid rgba(99,102,241,0.4)',
              color: '#a5b4fc',
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 14px rgba(99,102,241,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
          >
            <ChevronDown size={12} /> {t('clientDetail.changeStatus')}
          </button>
          <button onClick={loadClient}
            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/[0.07]' : 'text-gray-500 hover:bg-gray-100'}`}
            title={t('clientDetail.refresh')}>
            <RefreshCw size={14} />
          </button>
          {/* Actions dropdown */}
          <div className="relative" ref={actionsRef}>
            <button onClick={() => setShowActions(v => !v)}
              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/[0.07]' : 'text-gray-500 hover:bg-gray-100'}`}>
              <ChevronDown size={16} className={`transition-transform ${showActions ? 'rotate-180' : ''}`} />
            </button>
            {showActions && (
              <div className={`absolute right-0 top-full mt-1.5 w-48 rounded-xl shadow-xl border z-50 overflow-hidden ${isDark ? 'bg-[#0f1729] border-gray-700/60' : 'bg-white border-gray-200'}`}
                style={{ boxShadow: isDark ? '0 16px 40px rgba(0,0,0,0.5)' : '0 16px 40px rgba(0,0,0,0.12)' }}>
                {[
                  { icon: LogIn,     label: 'Login As',        action: async () => { setShowActions(false); try { const { data } = await api.post(`/clients/${id}/portal-login-as`); window.open(data.link, '_blank'); } catch (e) { const detail = e?.response?.data?.detail; if (detail) toast.error(detail); else if (!e?.response) toast.error('Network error — backend unreachable'); else toast.error(`Failed (${e.response.status})`); } } },
                  { icon: KeyRound,  label: 'Change Password', action: () => { setShowActions(false); setShowChangePassword(true); } },
                  { icon: Link,      label: 'Magic Link',      action: () => { setShowActions(false); setGeneratedLink(''); setShowMagicLink(true); } },
                  { icon: Users,     label: 'Transfer to agent', action: async () => { setShowActions(false); try { const { data } = await api.get('/users', { params: { limit: 500, active: true } }); setTransferUsers(data.users || []); } catch { setTransferUsers([]); } setShowTransfer(true); } },
                  { icon: ShieldOff, label: client.active ? 'Block Account' : 'Unblock Account', action: () => { setShowActions(false); setShowBlockConfirm(true); } },
                ].map(({ icon: Icon, label, action }) => (
                  <button key={label} onClick={action}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-300 hover:bg-white/[0.05]' : 'text-gray-700 hover:bg-gray-50'}`}>
                    <Icon size={14} />{label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* â•â• 3. INFO GRID + TABS â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <div className="p-4 space-y-4">

        <div className="grid grid-cols-3 gap-4">

          {/* ── Col 1: Personal Information ── */}
          <div className={section} style={{ position: 'relative' }} ref={fieldsConfigRef}>
            <div className={`${sectionHd} flex items-center justify-between`}>
              <span>{t('clientDetail.personalInformation')}</span>
              <button
                onClick={() => setShowFieldsConfig(v => !v)}
                title={t('clientDetail.configureVisibleFields')}
                className={`p-0.5 rounded transition-colors ${isDark ? 'text-gray-600 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </button>
            </div>

            {/* Fields config dropdown */}
            {showFieldsConfig && (
              <div
                className={`absolute right-0 top-8 z-30 w-52 rounded-lg border shadow-xl p-2 ${isDark ? 'bg-[#0f1729] border-gray-700' : 'bg-white border-gray-200'}`}
                style={{ top: '2rem' }}
              >
                <p className={`text-xs font-semibold px-1 pb-1 mb-1 border-b ${isDark ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-100'}`}>{t('clientDetail.visibleFields')}</p>
                {PI_FIELDS.map(f => (
                  <label key={f} className={`flex items-center gap-2 px-1 py-0.5 rounded cursor-pointer text-xs ${isDark ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}>
                    <input type="checkbox" checked={visibleFields.has(f)} onChange={() => toggleFieldVisibility(f)} className="accent-purple-500 w-3 h-3" />
                    {FIELD_LABELS[f] || f}
                  </label>
                ))}
              </div>
            )}

            <div className="py-1 overflow-y-auto" style={{ maxHeight: 320 }}>
              {[
                ['Date of birth', client.birthdate || client.birth_date],
                ['Gender',        client.gender],
                ['Phone',         client.phone],
                ['Email',         client.email],
                ['Country',       client.country],
                ['Language',      client.language],
                ['City',          client.city],
                ['Address',       client.address],
                ['Affiliate',     client.affiliate],
                ['Affiliate ID',  client.extId || client.affiliateId],
                ['Source',        client.source],
                ['Referral',      client.referral],
                ['Funnel',        client.funnel],
                ['Reg. IP',       client.registrationIp],
              ].filter(([label]) => visibleFields.has(label)).map(([label, val]) => (
                <div key={label} className={infoRow}>
                  <span className={infoLabel}>{FIELD_LABELS[label] || label}</span>
                  <span className={infoVal} title={val || '—'}>
                    {val
                      ? <span className="inline-flex items-center gap-1.5">
                          <span>{val}</span>
                          {label === 'Phone' && (
                            <span className="inline-flex">
                              <button
                                onClick={startTelephonyCall}
                                title={t('clientDetail.callClient')}
                                className="inline-flex items-center justify-center w-5 h-5 rounded-full transition-colors"
                                style={{ background: 'rgba(34,197,94,0.12)', color: isDark ? '#86efac' : '#16a34a' }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(34,197,94,0.25)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(34,197,94,0.12)'; }}>
                                <Phone size={10} />
                              </button>
                            </span>
                          )}
                        </span>
                      : <span style={{ color: isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db' }}>—</span>
                    }
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Col 2: Acquisition Status ── */}
          <div className={section}>
            <div className={sectionHd}>{t('clientDetail.acquisitionStatus')}</div>
            <div className="p-3">
              <DeptStatusCard
                deptLabel={t('clientDetail.sales')}
                deptColor={isDark ? 'text-blue-400' : 'text-blue-600'}
                accentBorder={isDark ? 'border-blue-500/60' : 'border-blue-400'}
                accentBg={isDark ? 'bg-blue-950/20' : 'bg-blue-50'}
                isActive={dept !== 'retention'}
                status={client.salesStatus || (dept !== 'retention' ? client.status : '')}
                agent={client.salesAgent || (dept !== 'retention' ? client.agent : null)}
                desk={client.salesDesk  || (dept !== 'retention' ? client.desk   : null)}
                team={client.salesTeam  || (dept !== 'retention' ? client.team   : null)}
                isDark={isDark}
                t={t}
              />
              <DeptStatusCard
                deptLabel={t('clientDetail.retention')}
                deptColor={isDark ? 'text-cyan-400' : 'text-cyan-600'}
                accentBorder={isDark ? 'border-cyan-500/60' : 'border-cyan-400'}
                accentBg={isDark ? 'bg-cyan-950/20' : 'bg-cyan-50'}
                isActive={dept === 'retention'}
                status={client.retStatus || (dept === 'retention' ? client.status : '')}
                agent={client.retAgent || (dept === 'retention' ? client.agent : null)}
                desk={client.retDesk   || (dept === 'retention' ? client.desk   : null)}
                team={client.retTeam   || (dept === 'retention' ? client.team   : null)}
                isDark={isDark}
                t={t}
              />
            </div>
          </div>

          {/* ── Col 3: Pinned Notes ── */}
          <div className={section}>
            <div className={sectionHd}>{t('clientDetail.pinnedNotes')}</div>
            <div className="px-3 py-1 overflow-y-auto" style={{ maxHeight: 320 }}>
              {pinnedComments.length === 0 ? (
                <p className={`text-xs text-center py-8 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{t('clientDetail.noPinnedNotes')}</p>
              ) : pinnedComments.map(c => (
                <div key={c.id} className={`py-2 border-b last:border-0 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                  <div className="flex items-start gap-1">
                    <Pin size={10} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className={`text-xs font-semibold truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{c.title}</p>
                      <p className={`text-xs mt-0.5 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{c.body}</p>
                      <p className={`text-xs mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{fmtDT(c.createdAt)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* ── TABS ── */}
        <div className={`rounded-xl border shadow-sm ${isDark ? 'bg-[#0f1729] border-gray-800/80' : 'bg-white border-gray-200'}`}>
          <div className={`flex border-b overflow-x-auto ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            {TABS.map(t => <Tab key={t} label={TAB_LABELS[t]} active={activeTab === t} onClick={() => setActiveTab(t)} isDark={isDark} />)}
          </div>

          <div className="p-5">

            {/* â•â•â•â• COMMENTS â•â•â•â• */}
            {activeTab === 'comments' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{t('clientDetail.comments')}</h3>
                  <button onClick={() => setShowNewComment(true)} className={cls.btnPrimary + ' flex items-center gap-1'}>
                    <Plus size={14} /> {t('clientDetail.newComment')}
                  </button>
                </div>
                {comments.length === 0 ? (
                  <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('clientDetail.noCommentsYet')}</p>
                ) : (
                  <div className="space-y-3">
                    {comments.map(c => (
                      <div key={c.id} className={`p-4 rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-gray-50 border-gray-200'} ${c.pinned ? 'border-l-4 border-l-yellow-500' : ''}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            {/* Header row: pin + topic + status badge */}
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              {c.pinned && <Pin size={12} className="text-yellow-500 flex-shrink-0" />}
                              <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.title}</span>
                            </div>
                            {/* Comment body */}
                            <p className={`text-sm whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{c.body}</p>
                            {/* Next call */}
                            {c.nextCallAt && (
                              <div className={`mt-2 flex items-center gap-1.5 text-xs font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                <Phone size={11} />
                                <span>Next call: {fmtDate(c.nextCallAt)}</span>
                              </div>
                            )}
                            {/* Footer: author + date */}
                            <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{c.author} · {fmtDate(c.createdAt)}</p>
                          </div>
                          {/* Action buttons — role-gated */}
                          <div className="flex gap-0.5 flex-shrink-0">
                            {canEditComment(userRole) && (
                              <button onClick={() => openEditComment(c)}
                                className={`p-1 rounded transition-colors ${isDark ? 'text-gray-600 hover:text-gray-300 hover:bg-gray-700/40' : 'text-gray-300 hover:text-gray-600 hover:bg-gray-100'}`}
                                title="Edit comment">
                                <Pencil size={13} />
                              </button>
                            )}
                            {canDeleteComment(userRole) && (
                              <button onClick={() => deleteComment(c.id)}
                                className="p-1 rounded text-red-500/40 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                                title="Delete comment">
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* â•â•â•â• CASE INFO â•â•â•â• */}
            {activeTab === 'caseInfo' && (
              <div className="space-y-5">
                <div className={`flex items-center gap-6 text-sm flex-wrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span>Email: <strong>{client.email}</strong></span>
                  {client.affiliate && (
                    <span>Affiliate: <strong className="text-purple-400">{client.affiliate}</strong></span>
                  )}
                  {client.extId && (
                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>ExtID: <code>{client.extId}</code></span>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Portfolio Overview</h3>
                    <button onClick={() => { setEditPortfolio(null); setPortfolioForm({ company: '', dateFrom: '', dateTo: '', industry: '', funding: 0, overallBalance: 0, withdrawals: 0 }); setShowNewPortfolio(true); }}
                      className={`${cls.btn} flex items-center gap-1`}>
                      <Plus size={14} /> Add Portfolio
                    </button>
                  </div>
                  {portfolios.length === 0 ? (
                    <p className={`text-sm py-6 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No portfolios yet</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {portfolios.map(p => (
                        <div key={p.id} className={`p-4 rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                            {[['Company', p.company], ['Industry', p.industry], ['Funding', fmtMoney(p.funding)], ['Overall Balance', fmtMoney(p.overallBalance)], ['Withdrawals', fmtMoney(p.withdrawals)], ['Dates', `${p.dateFrom || '—'} → ${p.dateTo || '—'}`]].map(([k, v]) => (
                              <div key={k}>
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{k}</p>
                                <p className={isDark ? 'text-gray-200' : 'text-gray-800'}>{v || '—'}</p>
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => { setEditPortfolio(p); setPortfolioForm({ company: p.company || '', dateFrom: p.dateFrom || '', dateTo: p.dateTo || '', industry: p.industry || '', funding: p.funding, overallBalance: p.overallBalance, withdrawals: p.withdrawals }); setShowNewPortfolio(true); }}
                              className={`flex-1 py-1.5 text-xs ${cls.btn}`}>Update</button>
                            <button onClick={() => deletePortfolio(p.id)} className="px-3 py-1.5 text-xs text-red-500 border border-red-500/40 rounded-md hover:bg-red-500/10">Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* â•â•â•â• TRANSACTIONS â•â•â•â• */}
            {activeTab === 'transactions' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Transactions</h3>
                  <button onClick={() => setShowNewTxn(true)} className={cls.btnPrimary + ' flex items-center gap-1'}>
                    <Plus size={14} /> New Transaction
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${isDark ? 'border-gray-800 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
                        {['ID', 'Type', 'Amount USD', 'Status', 'Description', 'Date'].map(h => (
                          <th key={h} className="text-left px-3 py-2 font-medium text-xs whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {clientTxns.length === 0 ? (
                        <tr><td colSpan={6} className={`text-center py-8 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No transactions</td></tr>
                      ) : clientTxns.map(t => (
                        <tr key={t.id} className={`border-b ${isDark ? 'border-gray-800/50 hover:bg-[#1a1f3a]/40' : 'border-gray-100 hover:bg-gray-50'}`}>
                          <td className={`px-3 py-2.5 font-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t.id.slice(0, 8)}…</td>
                          <td className="px-3 py-2.5">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.type === 'deposit' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                              {t.type}
                            </span>
                          </td>
                          <td className={`px-3 py-2.5 font-semibold ${t.type === 'deposit' ? (isDark ? 'text-green-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')}`}>
                            {t.type === 'deposit' ? '+' : '-'}${t.amount?.toFixed(2)}
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {t.status}
                            </span>
                          </td>
                          <td className={`px-3 py-2.5 text-xs max-w-[160px] truncate ${isDark ? 'text-gray-400' : 'text-gray-600'}`} title={t.description}>{t.description || '—'}</td>
                          <td className={`px-3 py-2.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{fmtDate(t.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* â•â•â•â• CLIENT (Case Management + Timeline + Portal) â•â•â•â• */}
            {activeTab === 'client' && (
              <div className="space-y-5">
                <div className={`inline-flex rounded-lg border p-1 ${isDark ? 'border-gray-800 bg-[#0f1729]' : 'border-gray-200 bg-gray-50'}`}>
                  {[
                    { id: 'portalAccess', label: 'Client Portal Access' },
                    { id: 'progress', label: 'Case Progress' },
                    { id: 'nextStep', label: 'Next step' },
                    { id: 'blocker', label: 'Blocker' },
                    { id: 'caseManagement', label: 'Case Management' },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setCabinetTab(tab.id)}
                      className={`h-8 rounded-md px-3 text-xs font-semibold transition-colors ${
                        cabinetTab === tab.id
                          ? (isDark ? 'bg-purple-600 text-white' : 'bg-white text-purple-700 shadow-sm')
                          : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-900')
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Case Management */}
                {cabinetTab === 'caseManagement' && (
                <div className="flex flex-col gap-5">
                <div className="order-2">
                  <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Case Management</h3>
                  <div className={`p-4 rounded-lg border space-y-4 ${isDark ? 'border-gray-800 bg-[#0f1729]' : 'border-gray-200 bg-gray-50'}`}>
                    {/* Crypto row */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Cryptocurrency</span>
                        <label className="flex items-center gap-1.5 cursor-pointer select-none">
                          <div
                            onClick={() => setCaseManagement(p => ({ ...p, cryptoShowToClient: !p.cryptoShowToClient }))}
                            className={`w-8 h-4 rounded-full transition-colors relative ${caseManagement.cryptoShowToClient ? 'bg-purple-600' : (isDark ? 'bg-gray-700' : 'bg-gray-300')}`}
                          >
                            <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${caseManagement.cryptoShowToClient ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </div>
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Show to client</span>
                        </label>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[['Cryptocurrency', 'cryptocurrency', 'e.g. Bitcoin', 'text', 'cryptocurrency'], ['Link (Blockchain)', 'blockchainLink', 'https://...', 'text', 'blockchainLink'], ['Amount (tokens)', 'cryptoAmount', '0.00', 'text', 'amount'], ['Source', 'cryptoSource', 'e.g. Binance', 'text', 'source'], ['Date Found', 'cryptoFoundAt', '', 'date', 'foundAt']].map(([label, key, ph, type, portalKey]) => {
                          const visible = isCaseFieldVisible('crypto', portalKey);
                          return (
                          <div key={key}>
                            <div className="flex items-center justify-between mb-1">
                              <label className={cls.label + ' mb-0'}>{label}</label>
                              <button type="button" onClick={() => toggleCaseFieldVisibility('crypto', portalKey)} disabled={!caseManagement.cryptoShowToClient}
                                title={!caseManagement.cryptoShowToClient ? 'Enable Show to client first' : (visible ? 'Visible to client - click to hide' : 'Hidden from client - click to show')}
                                className={`transition-colors disabled:opacity-25 ${visible ? 'text-purple-500 hover:text-purple-400' : 'text-gray-400 hover:text-gray-300'}`}>
                                {visible ? <Eye size={12} /> : <EyeOff size={12} />}
                              </button>
                            </div>
                            <input type={type} value={caseManagement[key] || ''} onChange={e => setCaseManagement(p => ({ ...p, [key]: e.target.value }))} placeholder={ph} className={cls.input} />
                            {key === 'cryptoAmount' && caseManagement.cryptoAmount && (
                              <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                {formatTokenAmount(caseManagement.cryptoAmount, caseManagement.cryptocurrency)}
                                {formatApproxUsd(caseManagement.cryptoAmount, caseManagement.cryptocurrency, cryptoPrices) ? ` · ${formatApproxUsd(caseManagement.cryptoAmount, caseManagement.cryptocurrency, cryptoPrices)}` : ''}
                              </p>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                    {/* Bank row */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Bank Transfer</span>
                        <label className="flex items-center gap-1.5 cursor-pointer select-none">
                          <div
                            onClick={() => setCaseManagement(p => ({ ...p, bankShowToClient: !p.bankShowToClient }))}
                            className={`w-8 h-4 rounded-full transition-colors relative ${caseManagement.bankShowToClient ? 'bg-purple-600' : (isDark ? 'bg-gray-700' : 'bg-gray-300')}`}
                          >
                            <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${caseManagement.bankShowToClient ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </div>
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Show to client</span>
                        </label>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[['Bank', 'bank', 'Bank name', 'text', 'bank'], ['Currency', 'bankCurrency', 'USD', 'text', 'currency'], ['Amount', 'bankAmount', '0.00', 'text', 'amount'], ['Account #', 'bankAccount', 'IBAN/Account', 'text', 'account'], ['Source', 'bankSource', 'Source', 'text', 'source'], ['Date Found', 'bankFoundAt', '', 'date', 'foundAt']].map(([label, key, ph, type, portalKey]) => {
                          const visible = isCaseFieldVisible('bank', portalKey);
                          return (
                          <div key={key}>
                            <div className="flex items-center justify-between mb-1">
                              <label className={cls.label + ' mb-0'}>{label}</label>
                              <button type="button" onClick={() => toggleCaseFieldVisibility('bank', portalKey)} disabled={!caseManagement.bankShowToClient}
                                title={!caseManagement.bankShowToClient ? 'Enable Show to client first' : (visible ? 'Visible to client - click to hide' : 'Hidden from client - click to show')}
                                className={`transition-colors disabled:opacity-25 ${visible ? 'text-purple-500 hover:text-purple-400' : 'text-gray-400 hover:text-gray-300'}`}>
                                {visible ? <Eye size={12} /> : <EyeOff size={12} />}
                              </button>
                            </div>
                            <input type={type} value={caseManagement[key] || ''} onChange={e => setCaseManagement(p => ({ ...p, [key]: e.target.value }))} placeholder={ph} className={cls.input} />
                          </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={saveCaseManagement} className={cls.btnPrimary}>
                        {editingCaseAsset ? 'Update Case Entry' : 'Add Case Entry'}
                      </button>
                      {editingCaseAsset && (
                        <button onClick={resetCaseManagementForm} className={cls.btn}>Cancel edit</button>
                      )}
                    </div>
                  </div>

                  {false && caseAssets.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Saved Cases ({caseAssets.length})</p>
                      {caseAssets.map(a => (
                        <div key={a.id} className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-[#0f1729]' : 'border-gray-200 bg-white'}`}>
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${a.assetType === 'crypto' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}`}>
                            {a.assetType}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                              {a.assetType === 'crypto'
                                ? `${a.cryptocurrency || '—'} · ${a.amount ?? '—'}${a.source ? ' · ' + a.source : ''}`
                                : `${a.bank || '—'} · ${a.amount ?? '—'} ${a.bankCurrency || ''}${a.source ? ' · ' + a.source : ''}`}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              {a.foundAt ? `Found: ${a.foundAt}` : 'No date'}
                              {a.createdAt ? ` · Added: ${new Date(a.createdAt).toLocaleDateString()}` : ''}
                            </p>
                          </div>
                          <button onClick={() => toggleAssetVisibility(a.id)}
                            className={`px-2 py-0.5 text-xs rounded transition-colors ${a.showToClient ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : (isDark ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200')}`}>
                            {a.showToClient ? 'ðŸ‘ Visible' : 'Hidden'}
                          </button>
                          <button onClick={() => deleteCaseAsset(a.id)}
                            className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30">Del</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-3">
                    {renderCaseAssetList(portalCaseAssets, 'Visible in client portal', 'Nothing is visible to the client yet')}
                  </div>
                </div>

                {/* Case Timeline */}
                <div className="order-1">
                  <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Case Timeline</h3>
                  <div className={`p-4 rounded-lg border ${isDark ? 'border-gray-800 bg-[#0f1729]' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className={cls.label}>Source</label>
                        <input value={timelineForm.source} onChange={e => setTimelineForm(p => ({ ...p, source: e.target.value }))} className={cls.input} placeholder="e.g. Court, Email" />
                      </div>
                      <div>
                        <label className={cls.label}>Case Status *</label>
                        <select value={timelineForm.caseStatus} onChange={e => setTimelineForm(p => ({ ...p, caseStatus: e.target.value }))} className={cls.input}>
                          <option value="">Select status…</option>
                          {timelineForm.caseStatus && !TIMELINE_STATUS_OPTIONS.includes(timelineForm.caseStatus) && (
                            <option value={timelineForm.caseStatus}>{timelineForm.caseStatus}</option>
                          )}
                          {TIMELINE_STATUS_OPTIONS.map(label => (
                            <option key={label} value={label}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={cls.label}>Date *</label>
                        <input type="date" value={timelineForm.eventDate} onChange={e => setTimelineForm(p => ({ ...p, eventDate: e.target.value }))} className={cls.input} />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className={cls.label}>Comment</label>
                      <textarea value={timelineForm.comment} onChange={e => setTimelineForm(p => ({ ...p, comment: e.target.value }))} className={cls.input} rows={3} placeholder="Describe progress details, negotiation results, or next steps…" />
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <div
                          onClick={() => setTimelineForm(p => ({ ...p, showToClient: !p.showToClient }))}
                          className={`w-8 h-4 rounded-full transition-colors relative ${timelineForm.showToClient ? 'bg-purple-600' : (isDark ? 'bg-gray-700' : 'bg-gray-300')}`}
                        >
                          <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${timelineForm.showToClient ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </div>
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Show to client</span>
                      </label>
                      <div className="flex items-center gap-2">
                        {editingTimelineEvent && (
                          <button onClick={resetTimelineForm} className={cls.btn}>Cancel edit</button>
                        )}
                        <button onClick={addTimelineEvent} className={cls.btnPrimary + ' flex items-center gap-1'}>
                          <Plus size={14} /> {editingTimelineEvent ? 'Update Event' : 'Add Event'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Timeline history */}
                  {false && timelineEvents.length > 0 && (
                    <div className={`mt-3 rounded-lg border overflow-hidden ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                      <button
                        onClick={() => setTimelineExpanded(p => !p)}
                        className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium ${isDark ? 'bg-[#0f1729] text-gray-300 hover:bg-[#111827]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
                      >
                        <span>History: {timelineEvents.length} event{timelineEvents.length !== 1 ? 's' : ''}</span>
                        <ChevronDown size={16} className={`transition-transform ${timelineExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      {timelineExpanded && (
                        <div className="divide-y divide-gray-800">
                          {timelineEvents.map(ev => (
                            <div key={ev.id} className={`px-4 py-3 flex gap-3 ${isDark ? 'bg-[#0a0e1a]' : 'bg-white'}`}>
                              <div className="flex-shrink-0 mt-0.5">
                                <div className="w-2 h-2 rounded-full bg-purple-500 mt-1.5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                  <span className={`text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{ev.caseStatus}</span>
                                  {ev.source && <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>· {ev.source}</span>}
                                  {ev.eventDate && <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>· {ev.eventDate}</span>}
                                  {ev.showToClient && <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">visible to client</span>}
                                </div>
                                {ev.comment && <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{ev.comment}</p>}
                                <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{ev.createdBy} · {fmtDate(ev.createdAt)}</p>
                              </div>
                              <button onClick={() => deleteTimelineEvent(ev.id)} className="text-red-500 hover:text-red-400 flex-shrink-0"><Trash2 size={14} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-1 xl:grid-cols-2 gap-3">
                    {renderTimelineList(portalTimelineEvents, 'Visible in client portal', 'No visible timeline events')}
                  </div>
                </div>

                {/* Client Portal Credentials */}
                </div>)}

                {cabinetTab === 'progress' && (
                <div>
                  <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Case Progress</h3>
                  <div className={`p-4 rounded-lg border space-y-3 ${isDark ? 'border-gray-800 bg-[#0f1729]' : 'border-gray-200 bg-gray-50'}`}>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      The client sees this as the case progress bar in the portal. Pick the milestone the case has actually reached.
                    </p>
                    <div className="space-y-2">
                      {CASE_PROGRESS_STAGES.map(opt => {
                        const active = caseStage === opt.stage;
                        return (
                          <button
                            key={String(opt.stage)}
                            type="button"
                            disabled={caseStageSaving}
                            onClick={() => !active && saveCaseStage(opt.stage)}
                            className={`w-full rounded-lg border px-3 py-2.5 text-left transition-colors disabled:opacity-60 ${
                              active
                                ? 'border-purple-500 bg-purple-600/10'
                                : (isDark ? 'border-gray-800 bg-[#141a33] hover:border-gray-600' : 'border-gray-200 bg-white hover:border-gray-400')
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <span className={`text-sm font-semibold ${active ? 'text-purple-500' : (isDark ? 'text-gray-200' : 'text-gray-800')}`}>{opt.label}</span>
                              <span className={`text-xs font-bold ${active ? 'text-purple-500' : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>{opt.percent}%</span>
                            </div>
                            <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{opt.desc}</p>
                          </button>
                        );
                      })}
                    </div>

                    <div className={`pt-3 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                      {blocker.text && blocker.showToClient ? (
                        <div className={`rounded-lg border p-3 ${isDark ? 'border-amber-500/30 bg-amber-500/10' : 'border-amber-200 bg-amber-50'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-xs font-bold uppercase tracking-wide text-amber-600">On Hold</p>
                              <p className={`mt-1 text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{blocker.text}</p>
                            </div>
                            <button
                              type="button"
                              onClick={releaseCaseHold}
                              disabled={holdBusy}
                              className={`${cls.btnPrimary} flex-shrink-0`}
                            >
                              {holdBusy ? '...' : 'Release Hold'}
                            </button>
                          </div>
                        </div>
                      ) : !holdForm.open ? (
                        <button
                          type="button"
                          onClick={() => setHoldForm(p => ({ ...p, open: true }))}
                          className={`w-full rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${isDark ? 'border-amber-500/40 text-amber-400 hover:bg-amber-500/10' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}
                        >
                          Put case on Hold
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <div>
                            <label className={cls.label}>Hold reason (published to the client's case timeline)</label>
                            <textarea
                              value={holdForm.reason}
                              onChange={e => setHoldForm(p => ({ ...p, reason: e.target.value }))}
                              className={cls.input}
                              rows={2}
                              placeholder="e.g. The bank requested an additional account statement"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className={cls.label}>Waiting on</label>
                              <select
                                value={holdForm.waitingOn}
                                onChange={e => setHoldForm(p => ({ ...p, waitingOn: e.target.value }))}
                                className={cls.input}
                              >
                                <option value="client">The client</option>
                                <option value="us">Our team</option>
                                <option value="third_party">Third party (bank, regulator...)</option>
                              </select>
                            </div>
                            <div>
                              <label className={cls.label}>Estimated time (optional)</label>
                              <input
                                value={holdForm.eta}
                                onChange={e => setHoldForm(p => ({ ...p, eta: e.target.value }))}
                                className={cls.input}
                                placeholder="e.g. 2-3 weeks"
                              />
                            </div>
                          </div>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                            After confirming you will be taken to Next step — describe what has to happen to lift the hold.
                          </p>
                          <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setHoldForm({ open: false, reason: '', waitingOn: 'third_party', eta: '' })} className={cls.btn}>Cancel</button>
                            <button type="button" onClick={setCaseHold} disabled={holdBusy} className={cls.btnPrimary}>{holdBusy ? '...' : 'Confirm Hold'}</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                )}

                {cabinetTab === 'nextStep' && (
                <div>
                  <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Next step</h3>
                  <div className={`p-4 rounded-lg border space-y-3 ${isDark ? 'border-gray-800 bg-[#0f1729]' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className={cls.label}>Title</label>
                        <input
                          value={nextStep.title}
                          onChange={e => setNextStep(p => ({ ...p, title: e.target.value }))}
                          className={cls.input}
                          placeholder="e.g. Upload updated verification documents"
                        />
                      </div>
                      <div>
                        <label className={cls.label}>Button label</label>
                        <input
                          value={nextStep.actionLabel}
                          onChange={e => setNextStep(p => ({ ...p, actionLabel: e.target.value }))}
                          className={cls.input}
                          placeholder="e.g. Continue"
                        />
                      </div>
                    </div>
                    <div>
                      <label className={cls.label}>Description</label>
                      <textarea
                        value={nextStep.description}
                        onChange={e => setNextStep(p => ({ ...p, description: e.target.value }))}
                        className={cls.input}
                        rows={4}
                        placeholder="Explain what the client should do next..."
                      />
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <div
                          onClick={() => setNextStep(p => ({ ...p, showToClient: !p.showToClient }))}
                          className={`w-8 h-4 rounded-full transition-colors relative ${nextStep.showToClient ? 'bg-purple-600' : (isDark ? 'bg-gray-700' : 'bg-gray-300')}`}
                        >
                          <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${nextStep.showToClient ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </div>
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Show to client</span>
                      </label>
                      <button onClick={saveNextStep} className={cls.btnPrimary}>Save Next step</button>
                    </div>
                  </div>
                </div>
                )}

                {cabinetTab === 'blocker' && (
                <div>
                  <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Blocker</h3>
                  <div className={`p-4 rounded-lg border space-y-3 ${isDark ? 'border-gray-800 bg-[#0f1729]' : 'border-gray-200 bg-gray-50'}`}>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Shown to the client as a prominent banner explaining why the case is on hold. Clear the text and save to remove the banner.
                    </p>
                    <div>
                      <label className={cls.label}>What is the case waiting for?</label>
                      <textarea
                        value={blocker.text}
                        onChange={e => setBlocker(p => ({ ...p, text: e.target.value }))}
                        className={cls.input}
                        rows={3}
                        placeholder="e.g. We are waiting for a bank statement covering March–May"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className={cls.label}>Waiting on</label>
                        <select
                          value={blocker.waitingOn}
                          onChange={e => setBlocker(p => ({ ...p, waitingOn: e.target.value }))}
                          className={cls.input}
                        >
                          <option value="">Not specified</option>
                          <option value="client">The client</option>
                          <option value="us">Our team</option>
                          <option value="third_party">Third party (bank, regulator...)</option>
                        </select>
                      </div>
                      <div>
                        <label className={cls.label}>Estimated time (optional)</label>
                        <input
                          value={blocker.eta}
                          onChange={e => setBlocker(p => ({ ...p, eta: e.target.value }))}
                          className={cls.input}
                          placeholder="e.g. 2-3 weeks"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <div
                          onClick={() => setBlocker(p => ({ ...p, showToClient: !p.showToClient }))}
                          className={`w-8 h-4 rounded-full transition-colors relative ${blocker.showToClient ? 'bg-purple-600' : (isDark ? 'bg-gray-700' : 'bg-gray-300')}`}
                        >
                          <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${blocker.showToClient ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </div>
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Show to client</span>
                      </label>
                      <button onClick={saveBlocker} className={cls.btnPrimary}>Save Blocker</button>
                    </div>
                  </div>
                </div>
                )}

                {cabinetTab === 'portalAccess' && (
                <div>
                  <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Client Portal Access</h3>
                  <div className={`p-4 rounded-lg border ${isDark ? 'border-gray-800 bg-[#0f1729]' : 'border-gray-200 bg-gray-50'}`}>
                    {portalCreds.login ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Login:</span>
                          <span className={`text-sm font-mono ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{portalCreds.login}</span>
                        </div>
                        {portalCreds.password && (
                          <div className={`p-3 rounded-lg border ${isDark ? 'border-yellow-500/30 bg-yellow-500/10' : 'border-yellow-200 bg-yellow-50'}`}>
                            <p className="text-xs text-yellow-600 font-medium mb-1">Password:</p>
                            <span className="text-sm font-mono font-bold text-yellow-600">{portalCreds.password}</span>
                          </div>
                        )}
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <button onClick={generatePortalCreds} disabled={portalCredsLoading} className={cls.btn + ' text-xs'}>
                            {portalCredsLoading ? 'Generating…' : 'Reset Password'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Grant this client access to their personal portal where they can view the case timeline, documents, and chat with you.
                        </p>
                        <button onClick={generatePortalCreds} disabled={portalCredsLoading} className={cls.btnPrimary + ' flex items-center gap-1'}>
                          <KeyRound size={14} /> {portalCredsLoading ? 'Creating…' : 'Create Portal Access'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                )}

              </div>
            )}

            {/* â•â•â•â• DOCUMENTS â•â•â•â• */}
            {activeTab === 'documents' && (
              <div>
                {/* sub-tabs */}
                <div className={`flex gap-1 mb-5 p-1 rounded-lg w-fit ${isDark ? 'bg-[#0f1729]' : 'bg-gray-100'}`}>
                  {[{id:'verification',label:'Verification'},{id:'files',label:'Files'},{id:'case',label:'Case'},{id:'invoices',label:'Invoices'}].map(t => (
                    <button key={t.id} onClick={() => setDocSubTab(t.id)}
                      className={`px-4 py-1.5 text-sm rounded-md transition-colors ${docSubTab === t.id ? 'bg-purple-600 text-white' : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')}`}>
                      {t.label}
                    </button>
                  ))}
                </div>

                {docSubTab === 'verification' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Client Documents</h3>
                      <button onClick={loadClientDocs} className={`${cls.btn} flex items-center gap-1 text-xs py-1`}><RefreshCw size={12} /></button>
                    </div>
                    {docsLoading ? (
                      <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading…</p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {DOC_TYPES.map(docType => {
                          // Top slot = agent-uploaded, OR client-uploaded that has been approved
                          // (declined/pending client uploads stay in the "Uploaded by client" list below)
                          const existing = clientDocs.filter(d => d.docType === docType && (
                            (d.uploaderRole || 'agent') === 'agent' || d.status === 'approved'
                          ));
                          const latest = existing[0];
                          const statusColors = {
                            approved: 'border-green-500/50 bg-green-500/5',
                            declined: 'border-red-500/50 bg-red-500/5',
                            pending: 'border-yellow-500/50 bg-yellow-500/5',
                          };
                          const statusText = {
                            approved: <span className="text-green-400 text-xs font-medium">✓ Approved</span>,
                            declined: <span className="text-red-400 text-xs font-medium">✗ Declined</span>,
                            pending: <span className="text-yellow-400 text-xs font-medium">Pending</span>,
                          };
                          return (
                            <div key={docType}
                              className={`p-4 rounded-lg border-2 text-center transition-colors ${latest ? statusColors[latest.status] : (isDark ? 'border-gray-700 hover:border-purple-600 bg-[#0f1729]' : 'border-gray-300 hover:border-purple-400 bg-gray-50')} ${isDark ? '' : ''}`}>
                              <FileText size={24} className={`mx-auto mb-2 ${latest ? (latest.status === 'approved' ? 'text-green-400' : latest.status === 'declined' ? 'text-red-400' : 'text-yellow-400') : (isDark ? 'text-gray-600' : 'text-gray-400')}`} />
                              <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{docType}</p>
                              {latest ? (
                                <div className="space-y-1.5">
                                  {statusText[latest.status]}
                                  <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{latest.fileName}</p>
                                  <div className="flex justify-center gap-1 pt-1">
                                    {latest.status !== 'approved' && (
                                      <button onClick={() => changeDocStatus(latest.id, 'approved')}
                                        className="px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400 hover:bg-green-500/30">Approve</button>
                                    )}
                                    {latest.status !== 'declined' && (
                                      <button onClick={() => changeDocStatus(latest.id, 'declined')}
                                        className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30">Decline</button>
                                    )}
                                    <button onClick={() => setPreviewDoc(latest)}
                                      className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30"
                                      title="Preview document">View</button>
                                    <button onClick={() => deleteClientDoc(latest.id)}
                                      className="px-2 py-0.5 text-xs rounded bg-gray-500/20 text-gray-400 hover:bg-gray-500/30">Del</button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <button
                                    onClick={() => { setUploadDocType(docType); docFileRef.current?.click(); }}
                                    disabled={uploadingDoc}
                                    className={`text-xs px-3 py-1 rounded-md border transition-colors ${isDark ? 'border-gray-600 text-gray-400 hover:border-purple-500 hover:text-purple-400' : 'border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-500'}`}>
                                    <Upload size={11} className="inline mr-1" />Upload
                                  </button>
                                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-700' : 'text-gray-300'}`}>Max 10 MB</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Client-uploaded documents (from portal) — split into Pending and Declined sections */}
                    {(() => {
                      const clientUploads = clientDocs.filter(d => d.uploaderRole === 'client' && DOC_TYPES.includes(d.docType));
                      const pendingDocs = clientUploads.filter(d => d.status === 'pending');
                      const declinedDocs = clientUploads.filter(d => d.status === 'declined');
                      const statusBadge = {
                        approved: <span className="text-green-400 text-xs font-medium">✓ Approved</span>,
                        declined: <span className="text-red-400 text-xs font-medium">✗ Declined</span>,
                        pending: <span className="text-yellow-400 text-xs font-medium">Pending</span>,
                      };
                      const renderRow = (doc) => (
                        <div key={doc.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-[#0f1729]' : 'border-gray-200 bg-white'}`}>
                          <FileText size={18} className={`flex-shrink-0 ${doc.status === 'approved' ? 'text-green-400' : doc.status === 'declined' ? 'text-red-400' : 'text-yellow-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                              {doc.fileName} <span className={`${isDark ? 'text-gray-500' : 'text-gray-400'} font-normal`}>({doc.docType})</span>
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              {doc.uploadedBy} · {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}
                            </p>
                          </div>
                          {statusBadge[doc.status]}
                          {doc.status !== 'approved' && (
                            <button onClick={() => changeDocStatus(doc.id, 'approved')}
                              className="px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400 hover:bg-green-500/30 flex-shrink-0">Approve</button>
                          )}
                          {doc.status !== 'declined' && (
                            <button onClick={() => changeDocStatus(doc.id, 'declined')}
                              className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 flex-shrink-0">Decline</button>
                          )}
                          <button onClick={() => setPreviewDoc(doc)}
                            className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 flex-shrink-0"
                            title="Preview document">View</button>
                          <button onClick={() => deleteClientDoc(doc.id)}
                            className="px-2 py-0.5 text-xs rounded bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 flex-shrink-0">Del</button>
                        </div>
                      );
                      return (
                        <>
                          {pendingDocs.length > 0 && (
                            <div className="mt-8">
                              <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                Uploaded by client <span className={`text-xs font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>· awaiting review</span>
                              </h3>
                              <div className="space-y-2">{pendingDocs.map(renderRow)}</div>
                            </div>
                          )}
                          {declinedDocs.length > 0 && (
                            <div className="mt-8">
                              <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                                Declined documents <span className={`text-xs font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>· {declinedDocs.length}</span>
                              </h3>
                              <div className="space-y-2">{declinedDocs.map(renderRow)}</div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {/* hidden file input */}
                    <input ref={docFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
                      onChange={e => { const f = e.target.files[0]; if (f && uploadDocType) handleDocUpload(f, uploadDocType); e.target.value = ''; }} />
                  </div>
                )}

                {docSubTab === 'files' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Files</h3>
                      <div className="flex gap-2">
                        <button onClick={() => filesFileRef.current?.click()} disabled={uploadingFiles}
                          className={`${cls.btnPrimary} flex items-center gap-1 text-xs py-1.5 px-3`}>
                          <Upload size={12} />{uploadingFiles ? 'Uploading…' : 'Upload File'}
                        </button>
                      </div>
                    </div>
                    <input ref={filesFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.zip" className="hidden"
                      onChange={e => { const f = e.target.files[0]; if (f) handleFilesUpload(f); e.target.value = ''; }} />
                    {docsLoading ? (
                      <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading…</p>
                    ) : (() => {
                      const files = clientDocs.filter(d => d.docType === 'file');
                      return files.length === 0 ? (
                        <p className={`text-sm text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No files yet</p>
                      ) : (
                        <div className="space-y-2">
                          {files.map(doc => (
                            <div key={doc.id} className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-[#0f1729]' : 'border-gray-200 bg-white'}`}>
                              <FileText size={18} className="text-blue-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{doc.fileName}</p>
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{doc.uploadedBy} · {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}</p>
                              </div>
                              <button onClick={async () => { await api.patch(`/documents/${doc.id}/visibility`); loadClientDocs(); }}
                                className={`px-2 py-0.5 text-xs rounded flex-shrink-0 transition-colors ${doc.showToClient ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : (isDark?'bg-gray-700 text-gray-400 hover:bg-gray-600':'bg-gray-100 text-gray-500 hover:bg-gray-200')}`}>
                                {doc.showToClient ? 'ðŸ‘ Visible' : 'Draft'}
                              </button>
                              <button onClick={() => setPreviewDoc(doc)} className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 flex-shrink-0" title="Preview document">View</button>
                              <button onClick={() => deleteClientDoc(doc.id)}
                                className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 flex-shrink-0">Del</button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {docSubTab === 'case' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Case Files</h3>
                      <button onClick={loadClientDocs} className={`${cls.btn} flex items-center gap-1 text-xs py-1`}><RefreshCw size={12} /></button>
                    </div>
                    {docsLoading ? (
                      <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading…</p>
                    ) : (() => {
                      const caseFiles = clientDocs.filter(d => d.docType === 'case_file');
                      return caseFiles.length === 0 ? (
                        <p className={`text-sm text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No case files yet. Client can upload files from the portal.</p>
                      ) : (
                        <div className="space-y-2">
                          {caseFiles.map(doc => (
                            <div key={doc.id} className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-[#0f1729]' : 'border-gray-200 bg-white'}`}>
                              <FileText size={18} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{doc.fileName}</p>
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{doc.uploadedBy} · {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}</p>
                              </div>
                              <button onClick={() => setPreviewDoc(doc)}
                                className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30"
                                title="Preview document">View</button>
                              
                              <button onClick={() => deleteClientDoc(doc.id)}
                                className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30">Del</button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {docSubTab === 'invoices' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Invoices</h3>
                      <div className="flex gap-2">
                        <button onClick={() => invoiceFileRef.current?.click()} disabled={uploadingInvoice}
                          className={`${cls.btnPrimary} flex items-center gap-1 text-xs py-1.5 px-3`}>
                          <Upload size={12} />{uploadingInvoice ? 'Uploading…' : 'Upload'}
                        </button>
                      </div>
                    </div>
                    <input ref={invoiceFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx" className="hidden"
                      onChange={e => { const f = e.target.files[0]; if (f) handleInvoiceUpload(f); e.target.value = ''; }} />
                    {docsLoading ? (
                      <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading…</p>
                    ) : (() => {
                      const invoices = clientDocs.filter(d => d.docType === 'invoice');
                      return invoices.length === 0 ? (
                        <p className={`text-sm text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No invoices yet</p>
                      ) : (
                        <div className="space-y-2">
                          {invoices.map(doc => (
                            <div key={doc.id} className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-[#0f1729]' : 'border-gray-200 bg-white'}`}>
                              <FileText size={18} className="text-purple-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{doc.fileName}</p>
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{doc.uploadedBy} · {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}</p>
                              </div>
                              <button onClick={async () => { await api.patch(`/documents/${doc.id}/visibility`); loadClientDocs(); }}
                                className={`px-2 py-0.5 text-xs rounded flex-shrink-0 transition-colors ${doc.showToClient ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : (isDark?'bg-gray-700 text-gray-400 hover:bg-gray-600':'bg-gray-100 text-gray-500 hover:bg-gray-200')}`}>
                                {doc.showToClient ? 'ðŸ‘ Visible' : 'Draft'}
                              </button>
                              <button onClick={() => setPreviewDoc(doc)} className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 flex-shrink-0" title="Preview document">View</button>
                              <button onClick={() => deleteClientDoc(doc.id)}
                                className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 flex-shrink-0">Del</button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}


            {/* UPLOAD PREVIEW (delete / save as template / publish to portal) */}
            {previewDoc && (
              <UploadPreviewModal
                doc={previewDoc}
                isDark={isDark}
                onClose={() => setPreviewDoc(null)}
                onDeleted={loadClientDocs}
                onTemplated={loadClientDocs}
                onPublished={loadClientDocs}
                onStatusChanged={loadClientDocs}
              />
            )}

            {/* â•â•â•â• PERSONAL INFO â•â•â•â• */}
            {activeTab === 'personalInfo' && (() => {
              // Compact inline-editable field — designed for 2-col grid
              const PIField = ({ label, field, value, type = 'text', options, wide }) => {
                const [editing, setEditing] = React.useState(false);
                const [val, setVal] = React.useState(value ?? '');
                React.useEffect(() => { setVal(value ?? ''); }, [value]);
                const save = async () => { await handleFieldSave(field, val); setEditing(false); };
                const cancel = () => { setVal(value ?? ''); setEditing(false); };
                return (
                  <div className={`group rounded-lg px-3 py-2.5 transition-colors ${wide ? 'col-span-2' : ''} ${isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-gray-50'}`}>
                    <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{label}</p>
                    {editing ? (
                      <div className="flex items-center gap-1">
                        {options ? (
                          <select value={val} onChange={e => setVal(e.target.value)} autoFocus
                            className={`flex-1 text-sm px-2 py-1 rounded-md border focus:outline-none focus:ring-1 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                            <option value="">—</option>
                            {options.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input type={type} value={val} onChange={e => setVal(e.target.value)} autoFocus
                            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
                            className={`flex-1 text-sm px-2 py-1 rounded-md border focus:outline-none focus:ring-1 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`} />
                        )}
                        <button onClick={save} className="p-1 rounded text-green-400 hover:bg-green-500/10 flex-shrink-0"><Check size={13} /></button>
                        <button onClick={cancel} className={`p-1 rounded flex-shrink-0 ${isDark ? 'text-gray-500 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'}`}><X size={13} /></button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-1 min-w-0">
                        <span className={`text-sm truncate ${value ? (isDark ? 'text-gray-200' : 'text-gray-800') : (isDark ? 'text-gray-700' : 'text-gray-300')}`}>
                          {value || '—'}
                        </span>
                        <button onClick={() => setEditing(true)}
                          className={`p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ${isDark ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
                          <Pencil size={11} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-gray-800/80' : 'border-gray-200'}`}>
                  {/* 2-column compact grid */}
                  <div className="grid grid-cols-2 divide-x divide-y" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9' }}>
                    <PIField label="Date of birth"  field="birthdate"      value={client.birthdate || client.birth_date} type="date" />
                    <PIField label="Gender"          field="gender"         value={client.gender}    options={['Male','Female','Other']} />
                    <PIField label="Phone"           field="phone"          value={client.phone} />
                    <PIField label="Email"           field="email"          value={client.email}     type="email" />
                    <PIField label="Country"         field="country"        value={client.country} />
                    <PIField label="Language"        field="language"       value={client.language} />
                    <PIField label="City"            field="city"           value={client.city} />
                    <PIField label="Address"         field="address"        value={client.address} />
                    <PIField label="Affiliate"       field="affiliate"      value={client.affiliate} />
                    <PIField label="Affiliate ID"    field="extId"          value={client.extId || client.affiliateId} />
                    <PIField label="Source"          field="source"         value={client.source} />
                    <PIField label="Referral"        field="referral"       value={client.referral} />
                    <PIField label="Funnel"          field="funnel"         value={client.funnel} />
                    <PIField label="Reg. IP"         field="registrationIp" value={client.registrationIp} />
                  </div>
                </div>
              );
            })()}

            {/* â•â•â•â• CHATS â•â•â•â• */}
            {activeTab === 'chats' && (
              <div className="flex flex-col" style={{ height: '50vh' }}>
                <div className={`flex items-center justify-between pb-3 border-b mb-3 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                  <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Client Chat</span>
                </div>
                <div className="flex-1 overflow-y-auto space-y-3 pr-1 mb-3">
                  {messages.length === 0 ? (
                    <p className={`text-sm text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No messages yet</p>
                  ) : messages.map(m => (
                    <div key={m.id} className={`flex ${m.sender === 'agent' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs px-3 py-2 rounded-lg text-sm ${m.sender === 'agent' ? 'bg-purple-600 text-white' : (isDark ? 'bg-[#1a1f3a] text-gray-200' : 'bg-gray-100 text-gray-800')}`}>
                        <p>{m.body}</p>
                        <p className={`text-xs mt-1 opacity-60`}>{fmtDate(m.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                  <div className={`flex gap-2 pt-2 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    <input value={msgInput} onChange={e => setMsgInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && sendMessage()}
                      placeholder="Message…"
                      className={cls.input + ' flex-1'} />
                  <button onClick={sendMessage} className={cls.btnPrimary + ' flex items-center gap-1'}>
                    <Send size={14} />
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* â•â• MODALS â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}

      {/* New Comment */}
      {showNewComment && (
        <Modal title="New Call Log" onClose={() => { setShowNewComment(false); setNewComment({ title: '', body: '', pinned: false, status: '', next_call_date: '', next_call_time: '' }); }} isDark={isDark} maxW="max-w-lg">
          <div className="space-y-4">
            {/* Topic */}
            <div>
              <label className={cls.label}>Topic *</label>
              <input
                value={newComment.title}
                onChange={e => setNewComment(p => ({ ...p, title: e.target.value }))}
                className={cls.input}
                placeholder="e.g. Discussed deposit, Follow-up call"
                autoFocus
              />
            </div>

            {/* Next Call — required */}
            <div>
              <label className={`${cls.label} flex items-center gap-1`}>
                <CalendarDays size={12} />
                Next Call *
              </label>
              <DateTimePicker
                date={newComment.next_call_date}
                time={newComment.next_call_time}
                onChangeDT={(d, t) => setNewComment(p => ({ ...p, next_call_date: d, next_call_time: t }))}
                isDark={isDark}
                placeholder="Pick date & time"
              />
            </div>

            {/* Comment body */}
            <div>
              <label className={cls.label}>Comment *</label>
              <textarea
                value={newComment.body}
                onChange={e => setNewComment(p => ({ ...p, body: e.target.value }))}
                className={cls.input}
                rows={4}
                placeholder="What was discussed, outcome, next steps…"
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newComment.pinned} onChange={e => setNewComment(p => ({ ...p, pinned: e.target.checked }))} className="w-4 h-4 accent-purple-500" />
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pin</span>
              </label>
              <button onClick={submitComment} className={cls.btnPrimary}>Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Comment */}
      {editingComment && (
        <Modal title="Edit Comment" onClose={() => setEditingComment(null)} isDark={isDark} maxW="max-w-lg">
          <div className="space-y-4">
            <div>
              <label className={cls.label}>Topic *</label>
              <input
                value={editCommentForm.title}
                onChange={e => setEditCommentForm(p => ({ ...p, title: e.target.value }))}
                className={cls.input}
                autoFocus
              />
            </div>
            <div>
              <label className={`${cls.label} flex items-center gap-1`}>
                <CalendarDays size={12} /> Next Call
              </label>
              <DateTimePicker
                date={editCommentForm.next_call_date}
                time={editCommentForm.next_call_time}
                onChangeDT={(d, t) => setEditCommentForm(p => ({ ...p, next_call_date: d, next_call_time: t }))}
                isDark={isDark}
                placeholder="Pick date & time"
              />
            </div>
            <div>
              <label className={cls.label}>Comment *</label>
              <textarea
                value={editCommentForm.body}
                onChange={e => setEditCommentForm(p => ({ ...p, body: e.target.value }))}
                className={cls.input}
                rows={4}
              />
            </div>
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editCommentForm.pinned}
                  onChange={e => setEditCommentForm(p => ({ ...p, pinned: e.target.checked }))}
                  className="w-4 h-4 accent-purple-500" />
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pin</span>
              </label>
              <div className="flex gap-2">
                <button onClick={() => setEditingComment(null)} className={cls.btn}>Cancel</button>
                <button onClick={submitEditComment} className={cls.btnPrimary}>Save</button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* New Transaction */}
      {showNewTxn && (
        <Modal title="New Transaction" onClose={() => setShowNewTxn(false)} isDark={isDark}>
          <div className="space-y-3">
            <div>
              <label className={cls.label}>Amount USD *</label>
              <input type="text" inputMode="decimal" value={newTxn.amount} onChange={e => setNewTxn(p => ({ ...p, amount: e.target.value }))} className={cls.input} placeholder="0.00" />
            </div>
            {canUseAdminTxnControls && (
              <div>
                <label className={cls.label}>Type *</label>
                <select value={newTxn.type} onChange={e => setNewTxn(p => ({ ...p, type: e.target.value }))} className={cls.input}>
                  <option value="deposit">Deposit</option>
                  <option value="withdrawal">Withdraw</option>
                </select>
              </div>
            )}
            <div>
              <label className={cls.label}>Description</label>
              <input value={newTxn.description} onChange={e => setNewTxn(p => ({ ...p, description: e.target.value }))} className={cls.input} placeholder="e.g. Card payment, Crypto transfer…" />
            </div>
            {canUseAdminTxnControls && (
              <div>
                <label className={cls.label}>Status</label>
                <select value={newTxn.status} onChange={e => setNewTxn(p => ({ ...p, status: e.target.value }))} className={cls.input}>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            )}
            <div className="flex justify-end">
              <button onClick={submitNewTxn} className={cls.btnPrimary}>Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Portfolio form */}
      {showNewPortfolio && (
        <Modal title={editPortfolio ? 'Edit Portfolio' : 'Add Portfolio'} onClose={() => { setShowNewPortfolio(false); setEditPortfolio(null); }} isDark={isDark} maxW="max-w-lg">
          <div className="grid grid-cols-2 gap-3">
            {[['company', 'Company'], ['industry', 'Industry'], ['dateFrom', 'Date From'], ['dateTo', 'Date To']].map(([k, l]) => (
              <div key={k}>
                <label className={cls.label}>{l}</label>
                <input value={portfolioForm[k]} onChange={e => setPortfolioForm(p => ({ ...p, [k]: e.target.value }))} className={cls.input} />
              </div>
            ))}
            {[['funding', 'Funding'], ['overallBalance', 'Overall Balance'], ['withdrawals', 'Withdrawals']].map(([k, l]) => (
              <div key={k}>
                <label className={cls.label}>{l}</label>
                <input type="number" value={portfolioForm[k]} onChange={e => setPortfolioForm(p => ({ ...p, [k]: parseFloat(e.target.value) || 0 }))} className={cls.input} />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setShowNewPortfolio(false); setEditPortfolio(null); }} className={cls.btn}>Cancel</button>
            <button onClick={submitPortfolio} className={cls.btnPrimary}>Save</button>
          </div>
        </Modal>
      )}

      {/* Change Password */}
      {showChangePassword && (
        <Modal title="Change Password" onClose={() => setShowChangePassword(false)} isDark={isDark}>
          <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Client ID: {client.id}</p>
          <div className="space-y-3">
            <div>
              <label className={cls.label}>New Password</label>
              <input type="password" value={pwForm.newPassword} onChange={e => setPwForm(p => ({ ...p, newPassword: e.target.value }))} className={cls.input} placeholder="Enter new password" />
            </div>
            <div>
              <label className={cls.label}>Confirm Password</label>
              <input type="password" value={pwForm.confirmPassword} onChange={e => setPwForm(p => ({ ...p, confirmPassword: e.target.value }))} className={cls.input} placeholder="Confirm new password" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={pwForm.forceLogout} onChange={e => setPwForm(p => ({ ...p, forceLogout: e.target.checked }))} className="w-4 h-4 accent-purple-500" />
              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Force logout from all devices</span>
            </label>
            <div className="flex justify-end">
              <button onClick={handleChangePassword} className={cls.btnPrimary}>Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Magic Link */}
      {showMagicLink && (
        <Modal title="Generate magic login link" onClose={() => setShowMagicLink(false)} isDark={isDark}>
          <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Client ID: {client.id}</p>
          {!generatedLink ? (
            <div className="space-y-3">
              <div className={`p-3 rounded text-xs ${isDark ? 'bg-yellow-900/20 border border-yellow-700/40 text-yellow-400' : 'bg-yellow-50 border border-yellow-200 text-yellow-700'}`}>
                Use only when needed. Auto-login links grant access without password.
              </div>
              <div>
                <label className={cls.label}>Expires (h/minutes)</label>
                <input type="number" value={magicForm.expires} onChange={e => setMagicForm(p => ({ ...p, expires: +e.target.value }))} className={cls.input} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={magicForm.oneTime} onChange={e => setMagicForm(p => ({ ...p, oneTime: e.target.checked }))} className="w-4 h-4 accent-purple-500" />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>One-time use</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={magicForm.revokePrev} onChange={e => setMagicForm(p => ({ ...p, revokePrev: e.target.checked }))} className="w-4 h-4 accent-purple-500" />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Revoke previous links</span>
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowMagicLink(false)} className={cls.btn}>Close</button>
                <button onClick={handleGenerateMagicLink} className={cls.btnPrimary}>Generate</button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <label className={cls.label}>Generated Link</label>
              <div className="flex gap-2">
                <input readOnly value={generatedLink} className={cls.input + ' text-xs'} />
                <button onClick={() => { navigator.clipboard.writeText(generatedLink); toast.success('Copied!'); }}
                  className={cls.btn + ' flex-shrink-0'}>Copy</button>
              </div>
              <div className="flex justify-end">
                <button onClick={() => setShowMagicLink(false)} className={cls.btn}>Close</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Block/Unblock confirm */}
      {showBlockConfirm && (
        <Modal title={client.active ? 'Block Account' : 'Unblock Account'} onClose={() => setShowBlockConfirm(false)} isDark={isDark}>
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Are you sure you want to {client.active ? 'block' : 'unblock'} client #{client.id}?
          </p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowBlockConfirm(false)} className={cls.btn}>Cancel</button>
            <button onClick={handleBlockToggle} className={`px-4 py-2 text-sm rounded-md text-white ${client.active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
              {client.active ? 'Block' : 'Unblock'}
            </button>
          </div>
        </Modal>
      )}

      {/* Change Status */}
      {showChangeStatus && (
        <ChangeStatusModal
          client={client}
          statusOptions={statusOptions}
          isDark={isDark}
          onSave={handleFieldSave}
          onClose={() => setShowChangeStatus(false)}
          userRole={userRole}
        />
      )}

      {/* Transfer to another agent */}
      {showTransfer && (
        <TransferAgentModal
          client={client}
          clientId={id}
          users={transferUsers}
          isDark={isDark}
          userRole={userRole}
          onClose={() => setShowTransfer(false)}
          onTransferred={loadClient}
        />
      )}
    </div>
  );
}

